import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatQuantityParts } from '../utils/unitDisplay';
// ReactDOM, IoChevronDown and the icons only the row used moved out with it.
import { ArrowUpRight, FileText, Bell, Printer, Search, Menu, Check, Utensils, Info } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import BackButton from './BackToDashboardButton';
import { exportSectionsToPDF } from '../utils/pdfExport';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Line } from 'react-chartjs-2';
import {
    Chart,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Filler,
    Legend,
} from 'chart.js';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/highcharts-3d';
import 'highcharts/modules/exporting';
import 'highcharts/modules/accessibility';
import './stockrepo.css';
import './Usage.css';
import { StandardSearch, highlightMatch } from '../styles/standardStyles';
import { sortBySearchPriority } from '../utils/searchPriority';
// The report's percentage is defined once, in this module, because the Dashboard
// carousel renders the same rows and must print the identical figure.
import { getItemMarginDisplay } from '../utils/itemWiseSalesMargin';
// One definition of an Item-Wise Sales Report row, shared with the Dashboard's
// "View Table" so both screens present the report identically.
import SalesItemRow, { isPlaceholderImage } from './ItemWiseSalesRow';
import GraphPopup from './GraphPopup';
import { useTranslate } from '../translations/useTranslate';
import StockExportReportModal from './StockExportReportModal';
import UnitMappingService from '../services/unitMappingService';
import NoDataPlaceholder from './NoDataPlaceholder';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler, Legend);

const CalendarIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
);

// Using FontAwesome for back arrow to standardize icon style across app

// API Base URL configuration (includes /api from env)
const API_BASE_URL = process.env.REACT_APP_API_URL;

const AppLoader = () => (
    <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(255,255,255,0.6)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }}>
        <div style={{
            display:'flex', 
            flexDirection:'column', 
            alignItems:'center', 
            gap:'20px'
        }}>
            <div style={{ position: 'relative', width: '95px', height: '95px' }}>
                <img 
                    src="/invexis-logo.png"
                    alt="Invexis"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '70px',
                        height: '70px',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        padding: '0px',
                        pointerEvents: 'none',
                        objectFit: 'contain',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                        zIndex: 1
                    }}
                />
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '70px',
                    height: '70px',
                    border: '6px solid #000000',
                    borderTop: '6px solid #f9c806',
                    borderRight: '6px solid #f9c806',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    zIndex: 16
                }}></div>
            </div>
        </div>
        <style>{`
            @keyframes spin { 
                0% { transform: translate(-50%, -50%) rotate(0deg); } 
                100% { transform: translate(-50%, -50%) rotate(360deg); } 
            }
        `}</style>
    </div>
);

// The popup shell is shared with the Dashboard's "View Table" — see GraphPopup.js.

// The Item-Wise Sales Report row, its expanded margin panel and the full cost-breakdown
// screen behind "View More" now live in ItemWiseSalesRow.js, because the Dashboard's
// "View Table" renders the SAME report and has to render it identically. Nothing about
// the markup or the calculations changed in the move.

// Re-added proper component wrapper after accidental removal during refactor
export const StockAndWastageReportsDetail = ({ onBack, selectedReportId }) => {
    const { language, t } = useTranslate();
    const reportRefs = useRef({});
    const containerRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);
    const [showExportSuccess, setShowExportSuccess] = useState(false);
    const [showStockExportPicker, setShowStockExportPicker] = useState(false);
    const [showAutoModal, setShowAutoModal] = useState(false);
    const [autoModalMessage, setAutoModalMessage] = useState('');
    const autoModalTimerRef = useRef(null);
    const [headerData, setHeaderData] = useState(null);
    const [itemWiseSalesData, setItemWiseSalesData] = useState([]);
    const [performanceSummaryData, setPerformanceSummaryData] = useState([]);
    const [rawMaterialStockData, setRawMaterialStockData] = useState([]);
    const [inventoryValuation, setInventoryValuation] = useState(null);
    const [wastageChartData, setWastageChartData] = useState(null);
    const [keyInsightsData, setKeyInsightsData] = useState(null);
    const [performanceAnalyticsData, setPerformanceAnalyticsData] = useState(null);
    const [rawMaterialConsumptionData, setRawMaterialConsumptionData] = useState([]);
    const [estimatedRealtimeData, setEstimatedRealtimeData] = useState(null);
    const [weeklyTrend, setWeeklyTrend] = useState([]);
    const [menuCategories, setMenuCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null); // null = "All Categories"
    const [globalSearch, setGlobalSearch] = useState('');
    const [pickupData, setPickupData] = useState([]);
    const [loadingPickup, setLoadingPickup] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedGraphPopup, setSelectedGraphPopup] = useState(null);
    const [popupData, setPopupData] = useState(null);
    const [modalSearch, setModalSearch] = useState('');
    const [rawMaterialSearch, setRawMaterialSearch] = useState('');
    
    // Filter modal data based on search term (for item-wise-sales)
    const filteredPopupData = useMemo(() => {
        if (!popupData || !Array.isArray(popupData)) return [];
        return sortBySearchPriority(popupData, modalSearch, (item) => item?.name || '');
    }, [popupData, modalSearch]);

    // Filter raw material data based on search term
    const filteredRawMaterialData = useMemo(() => {
        if (!popupData || !Array.isArray(popupData)) return [];
        if (selectedGraphPopup !== 'raw-material-stock') return popupData;
        return sortBySearchPriority(popupData, rawMaterialSearch, (item) => item?.name || '');
    }, [popupData, rawMaterialSearch, selectedGraphPopup]);

    // Pickup graph shown in fullscreen popup; no inline state required

    // Handle graph card click to open popup
    const handleGraphCardClick = (cardType, data, title) => {
        setSelectedGraphPopup(cardType);
        setPopupData(data);
    };

    // Close popup
    const closeGraphPopup = () => {
        setSelectedGraphPopup(null);
        setPopupData(null);
        setModalSearch('');
        setRawMaterialSearch('');
    };
    const [error, setError] = useState(null);

    const [period, setPeriod] = useState('today');
    const [customDate, setCustomDate] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null); // For week navigation

    /**
     * Which load is the current one.
     *
     * Changing the period restarts the fetch, but the previous one is still in flight. With
     * twelve requests per load and no guard, a slow response from the period the user just
     * left could land after the new one and repaint the report with data for the wrong dates.
     * Every load takes a ticket and only the holder of the latest ticket is allowed to write
     * state. (The Sales report does the same thing, via isLatestRequest.)
     */
    const requestSeqRef = useRef(0);

    useEffect(() => {
        return () => {
            if (autoModalTimerRef.current) {
                clearTimeout(autoModalTimerRef.current);
                autoModalTimerRef.current = null;
            }
        };
    }, []);

    const showAutoCloseModal = (message, durationMs = 1500) => {
        setAutoModalMessage(message || '');
        setShowAutoModal(true);

        if (autoModalTimerRef.current) {
            clearTimeout(autoModalTimerRef.current);
        }

        autoModalTimerRef.current = setTimeout(() => {
            setShowAutoModal(false);
            autoModalTimerRef.current = null;
        }, durationMs);
    };

    const getEnterDelayStyle = (delayMs = 0) => ({
        '--report-enter-delay': `${Math.min(Math.max(delayMs, 0), 420)}ms`,
    });

    const getStaggerDelayStyle = (index, baseDelay = 0, step = 55, maxDelay = 420) => (
        getEnterDelayStyle(Math.min(baseDelay + (index * step), maxDelay))
    );

    const formatDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };
    const formatDisplayDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${day} - ${m} - ${y}`;
    };

    // Last day of the report period currently on screen. The ingredient cost breakdown
    // anchors its own Week / Month windows to this — opening it from an August report
    // must not quietly show September prices just because today is September.
    const reportAnchorDate = (() => {
        if (selectedWeek && selectedWeek.isSingleDay) return formatDate(selectedWeek.start);
        if (selectedWeek && (selectedWeek.isWeekSelection || selectedWeek.isCustomRange)) {
            return formatDate(selectedWeek.end);
        }
        if (period === 'custom' && customDate && customDate.isMonthSelection) {
            return formatDate(new Date(customDate.year, customDate.month + 1, 0));
        }
        if (period === 'custom' && customDate) return formatDate(customDate);
        return formatDate(new Date());
    })();

    // Helper functions for week navigation
    const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? 6 : day - 1; // Monday as week start
        d.setDate(d.getDate() - diff);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const getWeekEnd = (date) => {
        const d = getWeekStart(date);
        d.setDate(d.getDate() + 6); // Sunday as week end
        d.setHours(23, 59, 59, 999);
        return d;
    };

    const navigateWeek = (direction) => {
        const baseDate = selectedWeek ? new Date(selectedWeek.start) : new Date();
        const newDate = new Date(baseDate);
        newDate.setDate(baseDate.getDate() + (direction * 7));
        
        const weekStart = getWeekStart(newDate);
        const weekEnd = getWeekEnd(newDate);
        
        setSelectedWeek({
            start: weekStart,
            end: weekEnd,
            isWeekSelection: true
        });
        setPeriod('custom');
    };

    const goToThisWeek = () => {
        const today = new Date();
        const weekStart = getWeekStart(today);
        const weekEnd = getWeekEnd(today);
        
        setSelectedWeek({
            start: weekStart,
            end: weekEnd,
            isWeekSelection: true
        });
        setPeriod('week');
    };

    const formatWeekDisplay = (week) => {
        if (!week) return '';
        const startDate = new Date(week.start);
        const endDate = new Date(week.end);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        const startMonth = monthNames[startDate.getMonth()];
        const endMonth = monthNames[endDate.getMonth()];
        const startDay = startDate.getDate();
        const endDay = endDate.getDate();
        const year = endDate.getFullYear();
        
        if (startMonth === endMonth) {
            return `${startMonth} ${startDay}-${endDay}, ${year}`;
        } else {
            return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
        }
    };

    // Helper: open pickup graph from actions menu
    const handleOpenPickupGraph = async () => {
        
        // Open popup immediately; if data not loaded, a loading state is shown
        setSelectedGraphPopup('pickup-report');
        
        if (!pickupData || pickupData.length === 0) {
            fetchPickupReport();
        }
    };

    // Helper: open estimated vs actual graph
    const handleOpenEstimatedVsActualGraph = () => {
        setSelectedGraphPopup('estimated-realtime');
        setPopupData(estimatedRealtimeData);
    };

    const handleOpenItemWiseGraph = () => {
        setPopupData(itemWiseSalesData);
        setSelectedGraphPopup('item-wise-sales');
    };

    const handleOpenRawMaterialGraph = () => {
        setPopupData(rawMaterialStockData);
        setSelectedGraphPopup('raw-material-stock');
    };

    useEffect(() => {
        if (selectedReportId && reportRefs.current[selectedReportId] && !loading) {
            setTimeout(() => {
                const element = reportRefs.current[selectedReportId];
                if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
            }, 300);
        }
    }, [selectedReportId, loading]);

    // Debug: Log when graph popup opens/closes
    useEffect(() => {
        if (selectedGraphPopup) {
            console.log('🎨 Graph popup opened:', selectedGraphPopup);
        }
    }, [selectedGraphPopup, popupData, pickupData]);

    useEffect(() => {
        const fetchAllData = async () => {
            const mySeq = ++requestSeqRef.current;
            try {
                setLoading(true);

                // Get businessId from localStorage
                const businessId = Number(localStorage.getItem('businessId'));
                if (!businessId) {
                    console.error('Missing businessId in localStorage');
                    return;
                }
                
                // Build query string based on period and customDate
                let qs = `?period=${period}&businessId=${businessId}`;
                
                // Handle week selection
                if (selectedWeek && selectedWeek.isWeekSelection) {
                    const weekStart = formatDate(selectedWeek.start);
                    const weekEnd = formatDate(selectedWeek.end);
                    qs = `?period=custom&weekStart=${weekStart}&weekEnd=${weekEnd}&businessId=${businessId}`;
                }
                // Handle custom date range (multi-day range from Dates tab)
                else if (selectedWeek && selectedWeek.isCustomRange && !selectedWeek.isSingleDay) {
                    const weekStart = formatDate(selectedWeek.start);
                    const weekEnd = formatDate(selectedWeek.end);
                    qs = `?period=custom&weekStart=${weekStart}&weekEnd=${weekEnd}&businessId=${businessId}`;
                }
                // Handle single day selection (from Dates tab)
                else if (selectedWeek && selectedWeek.isSingleDay) {
                    const singleDate = formatDate(selectedWeek.start);
                    qs = `?period=custom&date=${singleDate}&businessId=${businessId}`;
                }
                // Handle month selection
                else if (period === 'custom' && customDate && customDate.isMonthSelection) {
                    // Convert month object to monthYear parameter (YYYY-MM format)
                    const monthYear = `${customDate.year}-${String(customDate.month + 1).padStart(2, '0')}`;
                    qs = `?period=month&monthYear=${monthYear}&businessId=${businessId}`;
                } else if (period === 'custom' && customDate) {
                    // Handle single date selection (existing functionality)
                    const d = formatDate(customDate);
                    qs += `&start=${d}&end=${d}`;
                }
                
                // Add language parameter for translated ingredient/item names
                const langParam = `&language=${language}`;
                
                // ── ONE WAVE ────────────────────────────────────────────────────────
                // This was three sequential waves: three "primary" endpoints, then seven
                // "secondary" ones, then inventory valuation, then the weekly trend. Each
                // wave had to finish before the next was even issued, so the report cost the
                // SUM of the four slowest requests — and the spinner covered all of it,
                // because setLoading(false) only ran after the last wave. Nothing in wave two
                // depended on wave one's result; the split was arbitrary.
                //
                // Now every request leaves together and the report costs the single slowest
                // one. This is exactly what made the Sales report fast — see the "Fire ALL
                // API calls in parallel (was sequential waterfall)" block in SalesReport.js.
                //
                // Each request also carries its own fallback, so one failing section renders
                // empty instead of throwing away the eleven that succeeded. Before, a single
                // non-ok response threw and the whole report became an error page.
                const getJson = async (url, fallback) => {
                    try {
                        const res = await fetch(url);
                        if (!res.ok) return fallback;
                        return await res.json();
                    } catch (e) {
                        return fallback;
                    }
                };

                const wantsWeeklyTrend = Boolean(selectedWeek && selectedWeek.isWeekSelection);
                const weeklyTrendUrl = wantsWeeklyTrend
                    ? `${API_BASE_URL}/stockrepo/weekly-trend?weekStart=${formatDate(selectedWeek.start)}&weekEnd=${formatDate(selectedWeek.end)}&businessId=${businessId}`
                    : null;

                const [
                    headerRes, salesRes, stockRes,
                    analyticsRes, consumptionRes, performanceRes, wastageRes, insightsRes,
                    estRtRes, categoriesRes, valData, trendData
                ] = await Promise.all([
                    getJson(`${API_BASE_URL}/stockrepo/header-summary${qs}${langParam}`, null),
                    getJson(`${API_BASE_URL}/stockrepo/item-wise-sales${qs}${langParam}`, []),
                    getJson(`${API_BASE_URL}/stockrepo/raw-material-stock${qs}${langParam}`, []),
                    getJson(`${API_BASE_URL}/stockrepo/performance-analytics${qs}${langParam}`, null),
                    getJson(`${API_BASE_URL}/stockrepo/raw-material-consumption${qs}${langParam}`, []),
                    getJson(`${API_BASE_URL}/stockrepo/performance-summary${qs}${langParam}`, []),
                    getJson(`${API_BASE_URL}/stockrepo/wastage-comparison${qs}${langParam}`, null),
                    getJson(`${API_BASE_URL}/stockrepo/key-insights${qs}${langParam}`, {}),
                    getJson(`${API_BASE_URL}/stockrepo/estimated-vs-actual-plates${qs}${langParam}${selectedCategory ? `&categoryId=${selectedCategory}` : ''}`, null),
                    getJson(`${API_BASE_URL}/stockrepo/menu-categories?businessId=${businessId}`, null),
                    getJson(`${API_BASE_URL}/stockrepo/inventory-valuation${qs}${langParam}`, null),
                    weeklyTrendUrl ? getJson(weeklyTrendUrl, []) : Promise.resolve([])
                ]);

                // A newer period was selected while these were in flight — its results are
                // the ones that belong on screen, so drop these without touching state.
                if (mySeq !== requestSeqRef.current) return;

                // The headline is the one section that must not be silently zeroed: showing
                // ₹0 total sales as though it were measured is worse than saying it failed.
                if (!headerRes) {
                    throw new Error('Failed to load the stock report header summary');
                }

                const hdrTotalSales = Math.round(Number(headerRes.totalSales || 0));
                const hdrGrossProfit = Math.round(Number(headerRes.grossProfit || 0));
                setHeaderData({
                    title: 'Total Sales & Stock Report',
                    itemsSold: `${headerRes.itemsSoldCount || 0} sold`,
                    totalSales: hdrTotalSales,
                    grossProfit: hdrGrossProfit,
                    change: '12.5%',
                });
                setItemWiseSalesData(salesRes);
                
                // Helper function to convert unit names to abbreviations
                const formatUnit = (unit) => UnitMappingService.normalizeUnitSymbol(unit);
                
                // Helper function to auto-convert units when value >= 1000
                // Canonical, app-wide display: full names (Gram/Kilogram/Millilitre/Litre/Pcs), auto-scaled.
                const autoConvertUnit = (value, unit) => {
                    if (!Number.isFinite(value) || value === null || value === undefined) return { value: '--', unit: unit };
                    const parts = formatQuantityParts(value, unit);
                    return { value: parts.qty, unit: parts.unit };
                };
                
                setRawMaterialStockData(stockRes.map(i => {
                    const unit = formatUnit(i.unit || '');
                    const hasNew = Object.prototype.hasOwnProperty.call(i, 'before_qty') || Object.prototype.hasOwnProperty.call(i, 'after_qty');
                    // Format decimals properly (2 decimal places) instead of rounding to integers
                    const formatQty = (val) => {
                        const num = Number(val || 0);
                        if (!Number.isFinite(num)) return 0;
                        // Show up to 2 decimal places, remove trailing zeros
                        return parseFloat(num.toFixed(2));
                    };
                    const before = hasNew ? formatQty(i.before_qty) : formatQty(i.quantity);
                    const wastedVal = hasNew ? formatQty(i.wasted_qty) : 0;
                    const deductedVal = hasNew ? formatQty(i.deducted_qty) : null;
                    const after = hasNew ? formatQty(i.after_qty) : formatQty(i.quantity);
                    
                    // Apply auto-conversion for all quantities
                    const beforeConverted = autoConvertUnit(before, unit);
                    const wastedConverted = autoConvertUnit(wastedVal, unit);
                    const deductedConverted = deductedVal !== null ? autoConvertUnit(deductedVal, unit) : { value: '-', unit: unit };
                    const afterConverted = autoConvertUnit(after, unit);
                    
                    return {
                        name: i.name,
                        image: i.image_url || i.image,
                        before: beforeConverted.value,
                        beforeUnit: beforeConverted.unit,
                        wasted: wastedConverted.value,
                        wastedUnit: wastedConverted.unit,
                        deducted: deductedConverted.value,
                        deductedUnit: deductedConverted.unit,
                        after: afterConverted.value,
                        afterUnit: afterConverted.unit,
                        status: i.stock_level || 'Sufficient',
                        // ── Editorial Stock Report (₹ valuation) ──────────────────────
                        // Raw base-unit quantities + weighted-avg unit cost, kept in ONE
                        // consistent unit so quantity × unitCost values reconcile. The
                        // display fields above (auto-converted) are unchanged.
                        unitCost: Number(i.unit_cost || 0),
                        baseUnit: unit,
                        rawBefore: before,
                        rawDeducted: deductedVal !== null ? deductedVal : 0,
                        rawWasted: wastedVal,
                        rawAfter: after
                    };
                }));

                const roundedAnalytics = analyticsRes && analyticsRes.items ? {
                    total: Math.round(Number(analyticsRes.total || 0)),
                    items: analyticsRes.items.map(it => ({ ...it, value: Math.round(Number(it.value || 0)) }))
                } : analyticsRes;
                setPerformanceAnalyticsData(roundedAnalytics);
                setRawMaterialConsumptionData((consumptionRes || []).map(item => {
                    const total = Math.round(Number(item.total_stock || 0));
                    const consumed = Math.round(Number(item.total_consumed || 0));
                    let pct = 0; if (total > 0) pct = (consumed / total) * 100; else if (consumed > 0) pct = consumed;
                    return { name: item.ingredient_name || 'Unknown Item', quantity: consumed, unit: formatUnit(item.unit_name || 'kg'), consumption: Math.round(pct * 100) / 100, totalStock: total };
                }));
                setPerformanceSummaryData((performanceRes || []).map(r => ({ item: r.item, sales: Math.round(Number(r.sales || 0)), profit: Math.round(Number(r.profit || 0)), wastage: Math.round(Number(r.wastage || 0)) })));
                setWastageChartData(wastageRes ? { labels: wastageRes.labels, data: (wastageRes.data || []).map(v => Math.round(Number(v || 0))) } : wastageRes);
                const ki = { ...insightsRes };
                if (ki && ki.mostWasted && typeof ki.mostWasted.value === 'string') {
                    const num = Number(String(ki.mostWasted.value).replace(/[^0-9.-]/g, '')); ki.mostWasted.value = `₹${Math.round(Number.isFinite(num) ? num : 0)}`;
                }
                setKeyInsightsData(ki);
                const estimatedData = estRtRes && estRtRes.success ? estRtRes.data : [];
               setEstimatedRealtimeData(estimatedData);
               setMenuCategories(categoriesRes && categoriesRes.success ? categoriesRes.data : []);

                // Inventory valuation (4 financial cards) — fetched in the wave above. It
                // keeps its "leave the cards alone on failure" behaviour: null means the
                // request failed, and stale-but-real cards beat zeroed ones.
                if (valData) setInventoryValuation(valData);

                setWeeklyTrend(Array.isArray(trendData) ? trendData : []);
            } catch (err) {
                if (mySeq !== requestSeqRef.current) return;
                console.error('Failed to load report data:', err); setError(err.message);
            } finally {
                // Only the newest load may clear the spinner. An older load finishing later
                // would otherwise reveal a half-populated report while the current one is
                // still running.
                if (mySeq === requestSeqRef.current) setLoading(false);
            }
        };
        fetchAllData();
    }, [period, customDate, selectedWeek, selectedCategory, language]);

    // Fetch category pickup report for today
    const fetchPickupReport = async (selectedDate = null) => {
        try {
            setLoadingPickup(true);
            const businessId = localStorage.getItem('businessId');
            
            // Use selected date or default to TODAY (calendar-based approach)
            // Production plans are created for TODAY, so pickup report shows TODAY's data
            const today = new Date();
            
            // Handle case where onClick passes event object instead of date string
            let dateStr;
            if (typeof selectedDate === 'string' && selectedDate.length > 0) {
                dateStr = selectedDate; // Use provided date string
            } else {
                dateStr = today.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // Default to today (YYYY-MM-DD)
            }
            
            const response = await fetch(
                `${API_BASE_URL}/stockrepo/category-pickup-report?businessId=${businessId}&date=${dateStr}`
            );
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
                // Log items with actual sales
                const itemsWithSales = result.data.filter(item => parseFloat(item.actual_plates || 0) > 0);

                // Log items with estimates
                const itemsWithEstimates = result.data.filter(item => parseFloat(item.estimated_plates || 0) > 0);

                // Validate data before setting
                if (Array.isArray(result.data)) {
                    setPickupData(result.data);
                } else {
                    console.error('❌ Invalid pickup data format:', result.data);
                    setPickupData([]);
                    showAutoCloseModal('Invalid data format received from server');
                }
                // Don't open modal, data will be displayed inline
            } else {
                console.error('Failed to fetch pickup report:', result.error);
                setPickupData([]);
                showAutoCloseModal('Failed to load pickup report: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error fetching pickup report:', err);
            setPickupData([]);
            showAutoCloseModal('Error loading pickup report. Please try again.\n' + err.message);
        } finally {
            setLoadingPickup(false);
        }
    };

    const TopBar = ({ title }) => (
        <div className="top-bar" style={{ display: 'flex', alignItems: 'center' }}>
            <button 
                onClick={onBack} 
                className="back-button"
                style={{
                    background: 'transparent',
                    border: '1px solid #e2e8f0',
                    cursor: 'pointer',
                    padding: '6px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#1e293b',
                    fontSize: '18px',
                    marginRight: '10px',
                    marginLeft: '130px',
                    borderRadius: '8px'
                }}
            >
                <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className="top-bar-title" style={{ margin: 0, fontSize: '1.5rem' }}>
                {title}
            </h1>
        </div>
    );

    // Enhanced DatePickerCombined with Week/Month/Dates tabs and Range Mode
    const DatePickerCombined = ({ isOpen, onClose, onWeekSelect, onMonthSelect }) => {
        const [pickerMode, setPickerMode] = useState('dates'); // 'dates', 'week', or 'month'
        const [dateMode, setDateMode] = useState('single'); // 'single' or 'range' for Dates tab
        const [viewMonth, setViewMonth] = useState(new Date());
        const [viewYear, setViewYear] = useState(new Date().getFullYear());
        const [selectedStartDate, setSelectedStartDate] = useState(null);
        const [selectedEndDate, setSelectedEndDate] = useState(null);
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

        const currentToday = new Date();
        const currentYear = currentToday.getFullYear();
        const currentMonth = currentToday.getMonth();
        const isFutureMonthNav = viewMonth.getFullYear() > currentYear || 
            (viewMonth.getFullYear() === currentYear && viewMonth.getMonth() >= currentMonth);

        const navigateMonth = (direction) => {
            const newMonth = new Date(viewMonth);
            newMonth.setMonth(viewMonth.getMonth() + direction);
            setViewMonth(newMonth);
        };

        const navigateYear = (direction) => {
            setViewYear(viewYear + direction);
        };

        const formatWeekDisplay = (week) => {
            const startDay = week.start.getDate();
            const endDay = week.end.getDate();
            const startMonth = monthNames[week.start.getMonth()];
            const endMonth = monthNames[week.end.getMonth()];
            
            if (startMonth === endMonth) {
                return `${startDay} - ${endDay} ${startMonth}`;
            } else {
                return `${startDay} ${startMonth.substring(0, 3)} - ${endDay} ${endMonth.substring(0, 3)}`;
            }
        };

        const getWeeksInMonth = () => {
            const year = viewMonth.getFullYear();
            const month = viewMonth.getMonth();
            const firstDay = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);
            
            const weeks = [];
            let currentWeekStart = getWeekStart(firstDay);
            
            while (currentWeekStart <= lastDay) {
                const weekEnd = getWeekEnd(currentWeekStart);
                const weekStartDate = new Date(currentWeekStart);
                const weekEndDate = new Date(weekEnd);
                
                if (weekEndDate >= firstDay && weekStartDate <= lastDay) {
                    weeks.push({
                        start: new Date(weekStartDate),
                        end: new Date(weekEndDate)
                    });
                }
                
                currentWeekStart = new Date(currentWeekStart);
                currentWeekStart.setDate(currentWeekStart.getDate() + 7);
            }
            
            return weeks;
        };

        const handleWeekClick = (week) => {
            onWeekSelect({
                start: week.start,
                end: week.end,
                isWeekSelection: true
            });
            onClose();
        };

        const handleMonthClick = (monthIndex) => {
            onMonthSelect({ month: monthIndex, year: viewYear, isMonthSelection: true });
            onClose();
        };

        // Date selection functions - Mode-based selection (single or range)
        const handleDateClick = (date) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (date > today) return;
            
            if (dateMode === 'single') {
                const dateStart = new Date(date);
                dateStart.setHours(0, 0, 0, 0);
                const dateEnd = new Date(date);
                dateEnd.setHours(23, 59, 59, 999);
                
                onWeekSelect({
                    start: dateStart,
                    end: dateEnd,
                    isWeekSelection: false,
                    isCustomRange: true,
                    isSingleDay: true
                });
                onClose();
                return;
            }
            
            if (dateMode === 'range') {
                if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
                    setSelectedStartDate(date);
                    setSelectedEndDate(null);
                } else if (selectedStartDate && !selectedEndDate) {
                    if (date < selectedStartDate) {
                        setSelectedEndDate(selectedStartDate);
                        setSelectedStartDate(date);
                    } else {
                        setSelectedEndDate(date);
                    }
                }
            }
        };

        const applyDateRange = () => {
            if (selectedStartDate && selectedEndDate) {
                onWeekSelect({
                    start: selectedStartDate,
                    end: new Date(selectedEndDate.getTime() + 86400000 - 1),
                    isWeekSelection: false,
                    isCustomRange: true
                });
                setSelectedStartDate(null);
                setSelectedEndDate(null);
                onClose();
            }
        };

        const getDaysInMonth = () => {
            const year = viewMonth.getFullYear();
            const month = viewMonth.getMonth();
            const firstDay = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);
            const daysInMonth = lastDay.getDate();
            
            const days = [];
            const firstDayOfWeek = firstDay.getDay();
            const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
            
            for (let i = 0; i < offset; i++) {
                days.push(null);
            }
            
            for (let day = 1; day <= daysInMonth; day++) {
                days.push(new Date(year, month, day));
            }
            
            return days;
        };

        const isDateInRange = (date) => {
            if (!selectedStartDate || !selectedEndDate) return false;
            const time = date.getTime();
            return time >= selectedStartDate.getTime() && time <= selectedEndDate.getTime();
        };

        const isDateRangeStart = (date) => {
            if (!selectedStartDate) return false;
            return date.getTime() === selectedStartDate.getTime();
        };

        const isDateRangeEnd = (date) => {
            if (!selectedEndDate) return false;
            return date.getTime() === selectedEndDate.getTime();
        };

        const renderWeekGrid = () => {
            const today = new Date();
            const thisWeekStart = getWeekStart(today);
            const weeks = getWeeksInMonth();
            
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    marginTop: '15px',
                    maxHeight: '400px',
                    overflowY: 'auto'
                }}>
                    {weeks.map((week, index) => {
                        const weekStartTime = week.start.getTime();
                        const thisWeekTime = thisWeekStart.getTime();
                        const isThisWeek = weekStartTime === thisWeekTime;
                        const isFutureWeek = week.start > today;
                        const weekLabel = formatWeekDisplay(week);
                        
                        return (
                            <div
                                key={index}
                                className={`week-cell ${isThisWeek ? 'current-week' : ''} ${isFutureWeek ? 'disabled' : ''}`}
                                onClick={!isFutureWeek ? () => handleWeekClick(week) : undefined}
                                style={{
                                    padding: '15px 20px',
                                    textAlign: 'center',
                                    cursor: isFutureWeek ? 'not-allowed' : 'pointer',
                                    borderRadius: '8px',
                                    border: isThisWeek ? '2px solid #000' : '1px solid #e0e0e0',
                                    backgroundColor: isFutureWeek ? '#f5f5f5' : '#fff',
                                    color: isFutureWeek ? '#ccc' : '#333',
                                    fontWeight: isThisWeek ? 'bold' : '500',
                                    fontSize: '14px',
                                    transition: 'all 0.2s',
                                    opacity: isFutureWeek ? 0.5 : 1,
                                    pointerEvents: isFutureWeek ? 'none' : 'auto',
                                    boxShadow: 'none'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isFutureWeek && !isThisWeek) {
                                        e.target.style.backgroundColor = '#f5f5f5';
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isFutureWeek && !isThisWeek) {
                                        e.target.style.backgroundColor = '#fff';
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = 'none';
                                    }
                                }}
                            >
                                {isThisWeek && '✓ '}
                                {weekLabel}
                            </div>
                        );
                    })}
                </div>
            );
        };

        const renderMonthGrid = () => {
            const currentMonth = new Date().getMonth();
            const currentYear = new Date().getFullYear();
            const months = [];

            for (let i = 0; i < 12; i++) {
                const isCurrentMonth = (i === currentMonth && viewYear === currentYear);
                const isFutureMonth = (viewYear > currentYear) || (viewYear === currentYear && i > currentMonth);
                
                months.push(
                    <div
                        key={i}
                        className={`month-cell ${isCurrentMonth ? 'current' : ''} ${isFutureMonth ? 'future' : ''}`}
                        onClick={() => !isFutureMonth && handleMonthClick(i)}
                        style={{
                            padding: '20px',
                            textAlign: 'center',
                            cursor: isFutureMonth ? 'not-allowed' : 'pointer',
                            borderRadius: '8px',
                            border: isCurrentMonth ? '2px solid #000' : '1px solid #e0e0e0',
                            backgroundColor: isFutureMonth ? '#f5f5f5' : '#fff',
                            color: isFutureMonth ? '#ccc' : '#333',
                            fontWeight: isCurrentMonth ? 'bold' : 'normal',
                            transition: 'all 0.2s',
                            opacity: isFutureMonth ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                            if (!isFutureMonth && !isCurrentMonth) {
                                e.target.style.backgroundColor = '#f5f5f5';
                                e.target.style.transform = 'scale(1.05)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isFutureMonth && !isCurrentMonth) {
                                e.target.style.backgroundColor = '#fff';
                                e.target.style.transform = 'scale(1)';
                            }
                        }}
                    >
                        {monthNames[i].substring(0, 3)}
                    </div>
                );
            }

            return (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px',
                    marginTop: '20px'
                }}>
                    {months}
                </div>
            );
        };

        const renderDateCalendar = () => {
            const days = getDaysInMonth();
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            return (
                <div style={{ marginTop: '15px' }}>
                    {/* Month/Year header with Range toggle */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        marginBottom: '15px',
                        padding: '0 5px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
                            <button onClick={() => navigateMonth(-1)} style={{
                                padding: '6px 12px',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                border: 'none',
                                background: '#f5f5f5',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}>‹</button>
                            <span style={{ fontWeight: '600', fontSize: '15px', minWidth: '140px', textAlign: 'center' }}>
                                {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                            </span>
                            <button 
                                onClick={() => navigateMonth(1)} 
                                disabled={isFutureMonthNav}
                                style={{
                                    padding: '6px 12px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    border: 'none',
                                    background: '#f5f5f5',
                                    borderRadius: '6px',
                                    cursor: isFutureMonthNav ? 'not-allowed' : 'pointer',
                                    color: isFutureMonthNav ? '#ccc' : '#333',
                                    opacity: isFutureMonthNav ? 0.5 : 1
                                }}>›</button>
                        </div>
                        
                        <button 
                            onClick={() => {
                                setDateMode(dateMode === 'single' ? 'range' : 'single');
                                setSelectedStartDate(null);
                                setSelectedEndDate(null);
                            }}
                            style={{
                                padding: '8px 16px',
                                fontSize: '13px',
                                fontWeight: '600',
                                border: dateMode === 'range' ? '2px solid #000' : '2px solid #e0e0e0',
                                background: dateMode === 'range' ? '#f5f5f5' : '#fff',
                                color: dateMode === 'range' ? '#000' : '#666',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                whiteSpace: 'nowrap',
                                width: '100%'
                            }}
                        >
                            {dateMode === 'range' ? 'Range Mode' : 'Range Mode'}
                        </button>
                    </div>

                    {/* Day headers */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '5px',
                        marginBottom: '10px',
                        padding: '10px 0',
                        borderBottom: '1px solid #e0e0e0'
                    }}>
                        {dayNames.map(day => (
                            <div key={day} style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '12px', color: '#000' }}>
                                {day}
                            </div>
                        ))}
                    </div>
                    
                    {/* Calendar days */}
                    <div className="calendar-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '5px',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    }}>
                        {days.map((date, index) => {
                            if (!date) return <div key={`empty-${index}`} />;
                            
                            const isToday = date.getTime() === today.getTime();
                            const isFuture = date > today;
                            const inRange = isDateInRange(date);
                            const isStart = isDateRangeStart(date);
                            const isEnd = isDateRangeEnd(date);
                            // In RANGE mode we should not visually focus today by default.
                            const showTodayFocus = dateMode === 'single';
                            
                            return (
                                <div
                                    key={index}
                                    onClick={() => handleDateClick(date)}
                                    style={{
                                        padding: '10px',
                                        textAlign: 'center',
                                        cursor: isFuture ? 'not-allowed' : 'pointer',
                                        borderRadius: '4px',
                                        border: inRange || isStart || isEnd
                                            ? '2px solid #000'
                                            : (showTodayFocus && isToday ? '2px solid #000' : 'none'),
                                        backgroundColor: isFuture
                                            ? '#f9f9f9'
                                            : (inRange ? '#eef2ff' : (isStart || isEnd ? '#f5f5f5' : '#fff')),
                                        color: isFuture ? '#ccc' : '#333',
                                        fontWeight: (showTodayFocus && isToday) || inRange || isStart || isEnd ? 'bold' : 'normal',
                                        fontSize: '13px',
                                        opacity: isFuture ? 0.4 : 1,
                                        pointerEvents: isFuture ? 'none' : 'auto',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isFuture && !inRange && !isStart && !isEnd) {
                                            e.target.style.backgroundColor = '#f5f5f5';
                                            e.target.style.transform = 'scale(1.1)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isFuture && !inRange && !isStart && !isEnd) {
                                            e.target.style.backgroundColor = '#fff';
                                            e.target.style.transform = 'scale(1)';
                                        }
                                    }}
                                >
                                    {date.getDate()}
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* Apply button */}
                    {selectedStartDate && selectedEndDate && (
                        <div style={{ marginTop: '15px', textAlign: 'center' }}>
                            <button
                                onClick={applyDateRange}
                                style={{
                                    padding: '10px 30px',
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    transition: 'none'
                                }}
                            >
                                Apply Date Range
                            </button>
                        </div>
                    )}
                </div>
            );
        };

        if (!isOpen) return null;

        // Use CSS from Sales calendar for sizing; avoid inline overrides to keep behavior consistent
        const isMobile = typeof window !== 'undefined' && (window.innerWidth <= 768 || (window.matchMedia && window.matchMedia('(max-width: 768px)').matches));
        const popupStyle = {};

        return (
            <div className={`stk-date-picker-overlay ${isMobile ? 'mobile' : ''}`} onClick={onClose}>
                <div className={`stk-date-picker-popup ${isMobile ? 'mobile' : ''}`} onClick={e => e.stopPropagation()} style={popupStyle}>
                    {/* Tabs */}
                    <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '15px' }}>
                        <button onClick={() => setPickerMode('dates')} style={{
                            flex: 1, padding: '12px', border: 'none',
                            background: 'transparent',
                            color: pickerMode === 'dates' ? '#000' : '#666',
                            fontWeight: pickerMode === 'dates' ? 'bold' : 'normal',
                            fontSize: '1rem', cursor: 'pointer',
                            borderBottom: pickerMode === 'dates' ? '3px solid #000' : 'none',
                            marginBottom: '-2px',
                            transition: 'all 0.2s'
                        }}>Dates</button>
                        <button onClick={() => setPickerMode('week')} style={{
                            flex: 1, padding: '12px', border: 'none',
                            background: 'transparent',
                            color: pickerMode === 'week' ? '#000' : '#666',
                            fontWeight: pickerMode === 'week' ? 'bold' : 'normal',
                            fontSize: '1rem', cursor: 'pointer',
                            borderBottom: pickerMode === 'week' ? '3px solid #000' : 'none',
                            marginBottom: '-2px',
                            transition: 'all 0.2s'
                        }}>Week</button>
                        <button onClick={() => setPickerMode('month')} style={{
                            flex: 1, padding: '12px', border: 'none',
                            background: 'transparent',
                            color: pickerMode === 'month' ? '#000' : '#666',
                            fontWeight: pickerMode === 'month' ? 'bold' : 'normal',
                            fontSize: '1rem', cursor: 'pointer',
                            borderBottom: pickerMode === 'month' ? '3px solid #000' : 'none',
                            marginBottom: '-2px',
                            transition: 'all 0.2s'
                        }}>Month</button>
                    </div>

                    {/* Header for Week/Month modes */}
                    {pickerMode === 'week' && (
                        <div className="calendar-header">
                            <button onClick={() => navigateMonth(-1)} style={{
                                padding: '8px 12px', fontSize: '18px', fontWeight: 'bold',
                                border: 'none', background: '#f5f5f5', borderRadius: '6px', cursor: 'pointer'
                            }}>‹</button>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                                {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                            </h3>
                            <button 
                                onClick={() => navigateMonth(1)} 
                                disabled={isFutureMonthNav}
                                style={{
                                    padding: '8px 12px', fontSize: '18px', fontWeight: 'bold',
                                    border: 'none', background: '#f5f5f5', borderRadius: '6px', 
                                    cursor: isFutureMonthNav ? 'not-allowed' : 'pointer',
                                    color: isFutureMonthNav ? '#ccc' : '#333',
                                    opacity: isFutureMonthNav ? 0.5 : 1
                                }}>›</button>
                        </div>
                    )}
                    
                    {pickerMode === 'month' && (
                        <div className="calendar-header">
                            <button onClick={() => navigateYear(-1)} style={{
                                padding: '8px 12px', fontSize: '18px', fontWeight: 'bold',
                                border: 'none', background: '#f5f5f5', borderRadius: '6px', cursor: 'pointer'
                            }}>‹</button>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{viewYear}</h3>
                            <button 
                                onClick={() => navigateYear(1)} 
                                disabled={viewYear >= currentYear}
                                style={{
                                    padding: '8px 12px', fontSize: '18px', fontWeight: 'bold',
                                    border: 'none', background: '#f5f5f5', borderRadius: '6px', 
                                    cursor: viewYear >= currentYear ? 'not-allowed' : 'pointer',
                                    color: viewYear >= currentYear ? '#ccc' : '#333',
                                    opacity: viewYear >= currentYear ? 0.5 : 1
                                }}>›</button>
                        </div>
                    )}

                    {/* Render grid */}
                    {pickerMode === 'week' ? renderWeekGrid() :
                     pickerMode === 'month' ? renderMonthGrid() :
                     renderDateCalendar()}
                </div>
            </div>
        );
    };

    const DateSelector = ({ period, setPeriod, customDate, setCustomDate }) => {
        const [showWeekPicker, setShowWeekPicker] = useState(false);
        const [open, setOpen] = useState(false);
        const pickerRef = useRef(null);
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

        useEffect(() => {
            if (!open) return undefined;

            const handleOutsideClick = (event) => {
                if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                    setOpen(false);
                }
            };

            const handleEscape = (event) => {
                if (event.key === 'Escape') {
                    setOpen(false);
                }
            };

            document.addEventListener('mousedown', handleOutsideClick);
            document.addEventListener('keydown', handleEscape);

            return () => {
                document.removeEventListener('mousedown', handleOutsideClick);
                document.removeEventListener('keydown', handleEscape);
            };
        }, [open]);

        const handleSelect = (value) => {
            setPeriod(value);
            if (value !== 'custom') {
                setCustomDate(null);
                setSelectedWeek(null); // Clear week selection
            }
            if (value === 'week') {
                // Auto-select this week when "This Week" is clicked
                goToThisWeek();
            }
            setOpen(false);
        };

        const handleCalendarClick = () => {
            setOpen(false); // Close dropdown when calendar is opened
            setShowWeekPicker(true);
        };

        const handleWeekSelect = (week) => {
            setSelectedWeek(week);
            setPeriod('custom');
            setCustomDate(null); // Clear date selection
            setShowWeekPicker(false);
        };

        const handleMonthSelect = (monthData) => {
            setCustomDate(monthData);
            setPeriod('custom');
            setSelectedWeek(null); // Clear week selection
            setShowWeekPicker(false);
        };

        // Format display text based on week or date selection
        let customLabel = 'Custom Date';
        if (selectedWeek && selectedWeek.isWeekSelection) {
            customLabel = formatWeekDisplay(selectedWeek);
        } else if (selectedWeek && selectedWeek.isCustomRange) {
            // Handle custom date range from Dates tab
            if (selectedWeek.isSingleDay) {
                // Single day selection
                const singleDate = new Date(selectedWeek.start);
                customLabel = singleDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
            } else {
                // Multi-day range
                const startDate = new Date(selectedWeek.start);
                const endDate = new Date(selectedWeek.end);
                const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                customLabel = `${startStr} - ${endStr}`;
            }
        } else if (customDate) {
            if (customDate.isMonthSelection) {
                // Display month name and year (e.g., "October 2025")
                customLabel = `${monthNames[customDate.month]} ${customDate.year}`;
            } else {
                // Display full date for single day
                customLabel = customDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
            }
        }

        // Determine display label - FIXED: Now properly shows selected dates
        let displayLabel = t('common.allTime') || 'All Time';
        
        if (selectedWeek && (selectedWeek.isWeekSelection || selectedWeek.isCustomRange)) {
            // Week selection or custom date range
            displayLabel = customLabel;
        } else if (customDate) {
            // Month or single date selection
            displayLabel = customLabel;
        } else {
            // Standard period selections
            switch (period) {
                case 'today':
                    displayLabel = t('reports.today');
                    break;
                case 'week':
                    displayLabel = 'Week';
                    break;
                case 'month':
                    displayLabel = 'Month';
                    break;
                case 'year':
                    displayLabel = 'Year';
                    break;
                case 'all':
                default:
                    displayLabel = t('common.allTime') || 'All Time';
                    break;
            }
        }

        return (
            <>
                <div className="stk-top-picker" ref={pickerRef}>
                    <button
                        type="button"
                        className="stk-top-calendar-btn"
                        onClick={handleCalendarClick}
                        aria-label="Open calendar"
                    >
                        <CalendarIcon />
                    </button>
                    <button
                        type="button"
                        className="stk-top-period-trigger"
                        onClick={() => setOpen(!open)}
                        aria-haspopup="listbox"
                        aria-expanded={open}
                    >
                        <span className="stk-top-period-label">{displayLabel}</span>
                        <span className={`stk-top-chevron ${open ? 'open' : ''}`} />
                    </button>
                    {open && (
                        <div className="stk-top-period-menu">
                            <div className="stk-top-select-list">
                                <button type="button" className={`stk-top-period-option ${period === 'today' ? 'active' : ''}`} onClick={() => handleSelect('today')}>{t('reports.today')}</button>
                                <button type="button" className={`stk-top-period-option ${period === 'week' ? 'active' : ''}`} onClick={() => handleSelect('week')}>Week</button>
                                <button type="button" className={`stk-top-period-option ${period === 'month' ? 'active' : ''}`} onClick={() => handleSelect('month')}>Month</button>
                                <button type="button" className={`stk-top-period-option ${period === 'year' ? 'active' : ''}`} onClick={() => handleSelect('year')}>Year</button>
                                <button type="button" className={`stk-top-period-option ${period === 'all' ? 'active' : ''}`} onClick={() => handleSelect('all')}>{t('common.allTime') || 'All Time'}</button>
                                {(customDate || selectedWeek) && (
                                    <button type="button" className={`stk-top-period-option ${period === 'custom' ? 'active' : ''}`} onClick={() => handleSelect('custom')}>{customLabel}</button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <DatePickerCombined
                    isOpen={showWeekPicker}
                    onClose={() => setShowWeekPicker(false)}
                    onWeekSelect={handleWeekSelect}
                    onMonthSelect={handleMonthSelect}
                />
            </>
        );
    };

    // Week Navigator Component
    const WeekNavigator = () => {
        if (!selectedWeek || !selectedWeek.isWeekSelection) return null;

        const today = new Date();
        const thisWeekStart = getWeekStart(today);
        const selectedWeekStart = new Date(selectedWeek.start);
        const isThisWeek = selectedWeekStart.getTime() === thisWeekStart.getTime();
        const isFutureWeek = selectedWeekStart > thisWeekStart;

        return (
            <>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'stretch',
                gap: '12px',
                margin: '20px 0'
            }}>
                <button
                    onClick={() => navigateWeek(-1)}
                    style={{
                        flex: 1,
                        padding: '10px 16px',
                        minHeight: '48px',
                        backgroundColor: 'transparent',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f5f5f5';
                        e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.transform = 'translateY(0)';
                    }}
                >
                    ‹ Prv Week
                </button>
                
                <button
                    onClick={goToThisWeek}
                    disabled={isThisWeek}
                    style={{
                        flex: 1,
                        padding: '10px 16px',
                        minHeight: '48px',
                        backgroundColor: isThisWeek ? '#333' : 'transparent',
                        color: isThisWeek ? '#fff' : '#333',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        cursor: isThisWeek ? 'default' : 'pointer',
                        fontSize: '1rem',
                        fontWeight: '600',
                        transition: 'all 0.2s',
                        opacity: isThisWeek ? 0.95 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                        if (!isThisWeek) {
                            e.target.style.backgroundColor = '#f5f5f5';
                            e.target.style.transform = 'translateY(-2px)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isThisWeek) {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.transform = 'translateY(0)';
                        }
                    }}
                >
                    This Week
                </button>
                
                <button
                    onClick={() => navigateWeek(1)}
                    disabled={isFutureWeek}
                    style={{
                        flex: 1,
                        padding: '10px 16px',
                        minHeight: '48px',
                        backgroundColor: isFutureWeek ? '#f5f5f5' : 'transparent',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        cursor: isFutureWeek ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        opacity: isFutureWeek ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                        if (!isFutureWeek) {
                            e.target.style.backgroundColor = '#f5f5f5';
                            e.target.style.transform = 'translateY(-2px)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isFutureWeek) {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.transform = 'translateY(0)';
                        }
                    }}
                >
                    Next Week ›
                </button>
            </div>
            </>
        );
    };

    const SummaryBoxes = ({ data }) => (<div className="summary-boxes"><div className="summary-box"><p className="summary-label">Total Sales</p><p className="summary-value">₹{data.totalSales}</p><p className="summary-change"><ArrowUpRight size={14} className="summary-change-icon" />{data.change}</p></div><div className="summary-box"><p className="summary-label">Gross Profit</p><p className="summary-value">₹{data.grossProfit}</p><p className="summary-change"><ArrowUpRight size={14} className="summary-change-icon" />{data.change}</p></div></div>);

    const ItemWiseSalesCard = ({ data, isSelected, forwardedRef, onCardClick, onOpenGraph, animationDelay = 0 }) => {
        const [searchTerm, setSearchTerm] = useState('');
        const [menuOpen, setMenuOpen] = useState(false);
        const [sortBy, setSortBy] = useState('revenue');
        const menuRef = useRef(null);
        
        useEffect(() => {
            if (!menuOpen) return undefined;
            const handleOutsideClick = (e) => {
                if (menuRef.current && !menuRef.current.contains(e.target)) {
                    setMenuOpen(false);
                }
            };
            document.addEventListener('mousedown', handleOutsideClick);
            document.addEventListener('touchstart', handleOutsideClick);
            return () => {
                document.removeEventListener('mousedown', handleOutsideClick);
                document.removeEventListener('touchstart', handleOutsideClick);
            };
        }, [menuOpen]);
        
        // Use shared search-priority utility for consistent ranking across screens.
        const filteredData = useMemo(() => {
            const sorted = [...data].sort((a, b) => {
                if (sortBy === 'profit') {
                    const valA = a.grossProfitTotal != null ? a.grossProfitTotal : -Infinity;
                    const valB = b.grossProfitTotal != null ? b.grossProfitTotal : -Infinity;
                    return valB - valA;
                } else if (sortBy === 'margin') {
                    const valA = a.margin != null ? a.margin : -Infinity;
                    const valB = b.margin != null ? b.margin : -Infinity;
                    return valB - valA;
                } else { // 'revenue'
                    const valA = a.revenue != null ? a.revenue : 0;
                    const valB = b.revenue != null ? b.revenue : 0;
                    return valB - valA;
                }
            });
            return sortBySearchPriority(sorted, searchTerm, (item) => item?.name || '');
        }, [data, searchTerm, sortBy]);
        return (
            <div ref={forwardedRef} className={`${isSelected ? 'highlighted' : ''} report-enter-card`} style={getEnterDelayStyle(animationDelay)}>
                <div 
                    className="card item-sales-card"
                >
                    <div className="item-sales-card-inner">
                        <div className="item-sales-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 className="card-title-external" style={{textAlign:'left',paddingLeft:'0rem', marginTop: 0, marginBottom: 0, color: '#000000'}}>Item-Wise Sales Report</h3>
                            <div ref={menuRef} style={{ position: 'relative' }}>
                                <button
                                    type="button"
                                    onClick={() => setMenuOpen(!menuOpen)}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '6px',
                                        border: '1px solid #e5e7eb',
                                        background: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <Menu size={18} color="#6b7280" />
                                </button>
                                {menuOpen && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        marginTop: '4px',
                                        background: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                        minWidth: '180px',
                                        zIndex: 10,
                                        padding: '4px 0'
                                    }}>
                                        <div style={{ padding: '6px 12px', fontSize: '11px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>
                                            Sort By
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setSortBy('revenue'); setMenuOpen(false); }}
                                            style={{
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '8px 12px',
                                                background: sortBy === 'revenue' ? '#f3f4f6' : 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: 14,
                                                color: '#111827',
                                                fontWeight: sortBy === 'revenue' ? '600' : 'normal',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <span>Total Sales</span>
                                            {sortBy === 'revenue' && <span style={{ fontSize: '12px' }}>✓</span>}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setSortBy('profit'); setMenuOpen(false); }}
                                            style={{
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '8px 12px',
                                                background: sortBy === 'profit' ? '#f3f4f6' : 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: 14,
                                                color: '#111827',
                                                fontWeight: sortBy === 'profit' ? '600' : 'normal',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <span>Gross Profit Amount</span>
                                            {sortBy === 'profit' && <span style={{ fontSize: '12px' }}>✓</span>}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setSortBy('margin'); setMenuOpen(false); }}
                                            style={{
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '8px 12px',
                                                background: sortBy === 'margin' ? '#f3f4f6' : 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: 14,
                                                color: '#111827',
                                                fontWeight: sortBy === 'margin' ? '600' : 'normal',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <span>Margin (%)</span>
                                            {sortBy === 'margin' && <span style={{ fontSize: '12px' }}>✓</span>}
                                        </button>
                                        <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '4px 0' }} />
                                        <button
                                            type="button"
                                            onClick={() => { setMenuOpen(false); onOpenGraph && onOpenGraph(); }}
                                            style={{
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '8px 12px',
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: 14,
                                                color: '#111827',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <span>Detailed Costing</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="card-search-container">
                            <StandardSearch
                                value={searchTerm}
                                onChange={setSearchTerm}
                                onClear={() => setSearchTerm('')}
                                placeholder="Search items"
                                showClearButton={true}
                                style={{ marginBottom: '4px' }}
                            />
                        </div>
                        <div className="card-content-scrollable">
                        {filteredData.length === 0 ? (
                            searchTerm.trim() ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    minHeight: '300px',
                                    color: '#999',
                                    fontSize: '15px',
                                    textAlign: 'center',
                                    padding: '40px 20px'
                                }}>
                                    No items match your search
                                </div>
                            ) : (
                                <NoDataPlaceholder
                                    type="sales"
                                    title="No item sales yet"
                                    subtitle="A breakdown of sales by item appears here after your first sale."
                                    minHeight="260px"
                                />
                            )
                        ) : (
                            filteredData.map((item, index) => (
                                <div key={index} className="item-row-new report-enter-item" style={getStaggerDelayStyle(index, 120, 45, 340)}>
                                    <div className="item-row-left">
                                        {item.image && !isPlaceholderImage(item.image) ? (
                                            <>
                                                <img 
                                                    src={item.image} 
                                                    alt={item.name} 
                                                    className="item-image-new" 
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        const placeholder = e.target.parentNode.querySelector('.item-image-new-placeholder');
                                                        if (placeholder) placeholder.style.display = 'flex';
                                                    }}
                                                />
                                                <div className="item-image-new-placeholder" style={{ display: 'none' }}>
                                                    <Utensils size={18} className="placeholder-food-icon" />
                                                </div>
                                            </>
                                        ) : (
                                            <div className="item-image-new-placeholder">
                                                <Utensils size={18} className="placeholder-food-icon" />
                                            </div>
                                        )}
                                        <div className="item-details-new">
                                            <h4 className="item-name-new">{highlightMatch(item.name, searchTerm)}</h4>
                                            <p className="item-price-new">{item.price} • {item.sold}</p>
                                            <p className="item-wastage-new">{item.wastage}</p>
                                        </div>
                                    </div>
                                    <div className="item-profit-new">
                                        <p className="item-profit-value-new">
                                            <span className="currency-symbol">₹</span>
                                            <span  style={{color:'black'}} className="profit-amount">{String(item.grossProfit || '').replace('₹', '')}</span>
                                        </p>
                                        {/* The figure, its colour and its tooltip all come from
                                            getItemMarginDisplay() — the same call the Dashboard carousel
                                            makes — so the two screens cannot print different percentages
                                            for one item. Behaviour is unchanged: an estimated margin still
                                            carries "~", an unavailable one still renders "-". */}
                                        {(() => {
                                            const marginDisplay = getItemMarginDisplay(item);
                                            return (
                                                <p
                                                    className="item-profit-change-new"
                                                    style={{ color: marginDisplay.color }}
                                                    title={marginDisplay.title}
                                                >
                                                    {marginDisplay.text}
                                                </p>
                                            );
                                        })()}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    </div>
                </div>
            </div>
        );
    };

    const PerformanceSummaryCard = ({ data, isSelected, forwardedRef, onCardClick }) => {
        const [searchTerm, setSearchTerm] = useState('');
        
        // Use shared search-priority utility for consistent ranking across screens.
        const filteredData = useMemo(() => {
            return sortBySearchPriority(data, searchTerm, (row) => row?.item || '');
        }, [data, searchTerm]);
        return (
            <div ref={forwardedRef} className={`${isSelected ? 'highlighted' : ''}`}>
                <h3 className="card-title-external">Performance Summary</h3>
                <div 
                    className="card performance-summary-card"
                >
                    <div className="card-search-container">
                        <StandardSearch
                            value={searchTerm}
                            onChange={setSearchTerm}
                            onClear={() => setSearchTerm('')}
                            placeholder="Search Items"
                            showClearButton={true}
                            style={{ marginBottom: '16px' }}
                        />
                    </div>

                    {/* Fixed Header */}
                    <div className="performance-summary-header">
                        <div className="summary-list-header">
                            <span>Item</span>
                            <span>Sales (₹)</span>
                            <span>Profit (%)</span>
                            <span>Wastage (₹)</span>
                        </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className="performance-summary-content">
                        {filteredData.map((row, index) => (
                            <div key={index} className="summary-list-row-new">
                                <span className="summary-item-name">{highlightMatch(row.item, searchTerm)}</span>
                                <span className="summary-item-sales">{row.sales}</span>
                                <span className="summary-item-profit">{row.profit}</span>
                                <span className="summary-item-wastage">{row.wastage}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const RawMaterialStockCard = ({ data, isSelected, forwardedRef, onCardClick, onOpenGraph, animationDelay = 0 }) => {
        const [searchTerm, setSearchTerm] = useState('');
        const [menuOpen, setMenuOpen] = useState(false);
        const menuRef = useRef(null);

        useEffect(() => {
            if (!menuOpen) return undefined;
            const handleOutsideClick = (e) => {
                if (menuRef.current && !menuRef.current.contains(e.target)) {
                    setMenuOpen(false);
                }
            };
            document.addEventListener('mousedown', handleOutsideClick);
            document.addEventListener('touchstart', handleOutsideClick);
            return () => {
                document.removeEventListener('mousedown', handleOutsideClick);
                document.removeEventListener('touchstart', handleOutsideClick);
            };
        }, [menuOpen]);
        
        // Use shared search-priority utility for consistent ranking across screens.
        const filteredData = useMemo(() => {
            return sortBySearchPriority(data, searchTerm, (item) => item?.name || '');
        }, [data, searchTerm]);
        return (
            <div ref={forwardedRef} className={`${isSelected ? 'highlighted' : ''} report-enter-card`} style={getEnterDelayStyle(animationDelay)}>
                <div 
                    className="card raw-material-stock-card"
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 className="card-title-external" style={{textAlign:'left',paddingLeft:'0rem', marginTop: 0, marginBottom: 0}}>{t('reports.rawMaterialStockReport')}</h3>
                        <div ref={menuRef} style={{ position: 'relative' }}>
                            <button
                                type="button"
                                onClick={() => setMenuOpen(!menuOpen)}
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    background: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <Menu size={18} color="#6b7280" />
                            </button>
                            {menuOpen && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: '4px',
                                    background: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                    minWidth: '150px',
                                    zIndex: 10
                                }}>
                                    <button
                                        type="button"
                                        onClick={() => { setMenuOpen(false); onOpenGraph && onOpenGraph(); }}
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            padding: '10px 12px',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            color: '#111827'
                                        }}
                                    >
                                        View Table
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="card-search-container">
                        <StandardSearch
                            value={searchTerm}
                            onChange={setSearchTerm}
                            onClear={() => setSearchTerm('')}
                            placeholder={t('reports.searchMaterials')}
                            showClearButton={true}
                            style={{ marginBottom: '4px' }}
                        />
                    </div>
                    <div className="card-content-scrollable">
                        {filteredData.length === 0 ? (
                            searchTerm.trim() ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    minHeight: '200px',
                                    color: '#999',
                                    fontSize: '15px',
                                    textAlign: 'center',
                                    padding: '40px 20px'
                                }}>
                                    No materials match your search
                                </div>
                            ) : (
                                <NoDataPlaceholder
                                    type="stock"
                                    title="No stock data yet"
                                    subtitle="Add stock-in items or inventory count to view raw material stock."
                                    minHeight="220px"
                                />
                            )
                        ) : (
                            filteredData.map((item, index) => (
                                <div key={index} className="stock-item-card report-enter-item" style={getStaggerDelayStyle(index, 120, 45, 340)}>
                                    <div className="stock-item-header">
                                        <h4 className="stock-item-name">{highlightMatch(item.name, searchTerm)}</h4>
                                        <span className={`stock-item-status ${item.status === 'Sufficient' ? 'status-sufficient' : 'status-low'}`}>
                                            {item.status === 'Sufficient' ? t('reports.sufficient') : t('reports.lowStock')}
                                        </span>
                                    </div>
                                    <div className="stock-item-details">
                                        <div className="stock-detail-item">
                                            <span className="stock-detail-label">{t('reports.before')}</span>
                                            <span className="stock-detail-value">{item.before} {item.beforeUnit}</span>
                                        </div>
                                        <div className="stock-detail-item">
                                            <span className="stock-detail-label">{t('reports.deducted')}</span>
                                            <span className="stock-detail-value" style={{ color: '#f59e0b' }}>{item.deducted === '-' ? '-' : `${item.deducted} ${item.deductedUnit}`}</span>
                                        </div>
                                        <div className="stock-detail-item">
                                            <span className="stock-detail-label">{t('reports.wasted')}</span>
                                            <span className="stock-detail-value" style={{ color: '#ef4444' }}>{item.wasted} {item.wastedUnit}</span>
                                        </div>
                                        <div className="stock-detail-item">
                                            <span className="stock-detail-label">{t('reports.after')}</span>
                                            <span className="stock-detail-value">{item.after} {item.afterUnit}</span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const KeyInsightsCard = ({ data, isSelected, forwardedRef }) => {
        if (!data) return null;
        return (
            <div ref={forwardedRef} className={`${isSelected ? 'highlighted' : ''}`}>
                <h3 className="card-title-external">{t('reports.keyInsights')}</h3>
                <div className="card insights-card">
                    <div className="insight-row"><p className="insight-label">{t('reports.bestSellingItem')}:</p><p className="insight-value">{data.bestSelling?.item} ({data.bestSelling?.value})</p></div>
                    <div className="insight-row"><p className="insight-label">{t('reports.mostWastedItem')}:</p><p className="insight-value">{data.mostWasted?.item} {data.mostWasted?.value}</p></div>
                </div>
            </div>
        );
    };

    const handleExport = async () => {
        if (isExporting) return;
        try {
            setIsExporting(true);

            const formatDDMMYYYY = (d) => {
                if (!d) return '';
                const dt = (d instanceof Date) ? d : new Date(d);
                if (Number.isNaN(dt.getTime())) return '';
                const dd = String(dt.getDate()).padStart(2, '0');
                const mm = String(dt.getMonth() + 1).padStart(2, '0');
                const yyyy = String(dt.getFullYear());
                return `${dd}-${mm}-${yyyy}`;
            };

            const periodLabel = (() => {
                // Requirement: show only date(s) in dd-mm-yyyy (no "Today" / no brackets)
                if (period === 'custom' && customDate) return formatDDMMYYYY(customDate);
                if (selectedWeek && selectedWeek.isWeekSelection && selectedWeek.start && selectedWeek.end) {
                    return `${formatDDMMYYYY(selectedWeek.start)} - ${formatDDMMYYYY(selectedWeek.end)}`;
                }
                if (period === 'today') return formatDDMMYYYY(new Date());
                // Fallback: keep previous label if we don't have a concrete date
                return String(period || '');
            })();

            const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 15;
            const headerHeight = 10;
            let currentY = margin + headerHeight + 4;

            // Helper function to add header
            const drawHeader = () => {
                // Match Sales Report header layout
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(16);
                pdf.text('Stock & Wastage Report', margin, margin);
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(10);
                pdf.text(`Period: ${periodLabel}`, pageWidth - margin, margin, { align: 'right' });

                // Divider under header
                pdf.setDrawColor(0, 0, 0);
                pdf.setLineWidth(0.3);
                pdf.line(margin, margin + headerHeight - 2, pageWidth - margin, margin + headerHeight - 2);
            };

            drawHeader();

            // Helper to check if we need a new page
            const checkNewPage = (neededSpace) => {
                if (currentY + neededSpace > pageHeight - margin) {
                    pdf.addPage();
                    drawHeader();
                    currentY = margin + headerHeight + 4;
                }
            };

            // 1. Item-Wise Sales Report
            if (itemWiseSalesData && itemWiseSalesData.length > 0) {
                checkNewPage(20);
                pdf.setFontSize(14);
                pdf.setFont('helvetica', 'bold');
                pdf.text('Item-Wise Sales Report', margin, currentY);
                currentY += 8;

                const salesTableData = itemWiseSalesData.map(item => [
                    item.name || '-',
                    item.price || '-',
                    item.grossProfit || '-',
                    item.margin != null ? `${item.margin.toFixed(1)}%` : '-'
                ]);

                autoTable(pdf, {
                    startY: currentY,
                    head: [['Item', 'Price & Quantity', 'Total Amount', 'Margin']],
                    body: salesTableData,
                    theme: 'plain',
                    willDrawPage: () => drawHeader(),
                    headStyles: { 
                        fillColor: [255, 255, 255], 
                        textColor: [0, 0, 0], 
                        fontStyle: 'bold',
                        lineWidth: 0.5,
                        lineColor: [0, 0, 0]
                    },
                    styles: { 
                        fontSize: 9, 
                        cellPadding: 3,
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0]
                    },
                    columnStyles: {
                        0: { cellWidth: 50 },
                        1: { cellWidth: 50 },
                        2: { cellWidth: 40 },
                        3: { cellWidth: 40 }
                    },
                    margin: { top: margin + headerHeight + 4, left: margin, right: margin },
                    didDrawPage: (data) => {
                        currentY = data.cursor.y + 5;
                    }
                });

                currentY = pdf.lastAutoTable.finalY + 10;
            }

            // 2. Raw Material Stock Report
            if (rawMaterialStockData && rawMaterialStockData.length > 0) {
                checkNewPage(20);
                pdf.setFontSize(14);
                pdf.setFont('helvetica', 'bold');
                pdf.text('Raw Material Stock Report', margin, currentY);
                currentY += 8;

                const stockTableData = rawMaterialStockData.map(item => [
                    item.name || '-',
                    item.before || '-',
                    item.deducted || '-',
                    item.wasted || '-',
                    item.after || '-',
                    item.status || '-'
                ]);

                autoTable(pdf, {
                    startY: currentY,
                    head: [['Material', 'Before', 'Deducted', 'Wasted', 'After', 'Status']],
                    body: stockTableData,
                    theme: 'plain',
                    willDrawPage: () => drawHeader(),
                    headStyles: { 
                        fillColor: [255, 255, 255], 
                        textColor: [0, 0, 0], 
                        fontStyle: 'bold',
                        lineWidth: 0.5,
                        lineColor: [0, 0, 0]
                    },
                    styles: { 
                        fontSize: 8, 
                        cellPadding: 2.5,
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0]
                    },
                    columnStyles: {
                        0: { cellWidth: 40 },
                        1: { cellWidth: 25 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 25 },
                        4: { cellWidth: 25 },
                        5: { cellWidth: 30 }
                    },
                    margin: { top: margin + headerHeight + 4, left: margin, right: margin },
                    didDrawPage: (data) => {
                        currentY = data.cursor.y + 5;
                    }
                });

                currentY = pdf.lastAutoTable.finalY + 10;
            }

            // 3. Estimated vs Real-Time Usage
            if (estimatedRealtimeData && Array.isArray(estimatedRealtimeData) && estimatedRealtimeData.length > 0) {
                checkNewPage(20);
                pdf.setFontSize(14);
                pdf.setFont('helvetica', 'bold');
                pdf.text('Estimated vs Real-Time Usage', margin, currentY);
                currentY += 8;

                const usageTableData = estimatedRealtimeData.map(item => [
                    item.menu_item || '-',
                    `${item.estimated || 0} plates`,
                    `${item.actual_plates || 0} plates`,
                    `${((item.actual_plates || 0) - (item.estimated || 0))} variance`
                ]);

                autoTable(pdf, {
                    startY: currentY,
                    head: [['Menu Item', 'Estimated', 'Actual', 'Variance']],
                    body: usageTableData,
                    theme: 'plain',
                    willDrawPage: () => drawHeader(),
                    headStyles: { 
                        fillColor: [255, 255, 255], 
                        textColor: [0, 0, 0], 
                        fontStyle: 'bold',
                        lineWidth: 0.5,
                        lineColor: [0, 0, 0]
                    },
                    styles: { 
                        fontSize: 9, 
                        cellPadding: 3,
                        lineWidth: 0.1,
                        lineColor: [0, 0, 0]
                    },
                    columnStyles: {
                        0: { cellWidth: 70 },
                        1: { cellWidth: 35 },
                        2: { cellWidth: 35 },
                        3: { cellWidth: 30 }
                    },
                    margin: { top: margin + headerHeight + 4, left: margin, right: margin },
                    didDrawPage: (data) => {
                        currentY = data.cursor.y + 5;
                    }
                });
            }

            // Add page numbers
            const pageCount = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                pdf.setPage(i);
                pdf.setFontSize(9);
                pdf.setFont('helvetica', 'normal');
                pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 20, pageHeight - 10);
            }

            const fileName = `Stock_Report_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.pdf`;
            setShowExportSuccess(true);
            pdf.save(fileName);
        } catch (e) {
            console.error('PDF export failed:', e);
        } finally {
            setIsExporting(false);
        }
    };

    // Compute periodLabel for export modal
    const exportPeriodLabel = (() => {
        const formatDDMMYYYY = (d) => {
            if (!d) return '';
            const dt = (d instanceof Date) ? d : new Date(d);
            if (Number.isNaN(dt.getTime())) return '';
            const dd = String(dt.getDate()).padStart(2, '0');
            const mm = String(dt.getMonth() + 1).padStart(2, '0');
            const yyyy = String(dt.getFullYear());
            return `${dd}-${mm}-${yyyy}`;
        };
        if (period === 'custom' && customDate) return formatDDMMYYYY(customDate);
        if (selectedWeek && selectedWeek.isWeekSelection && selectedWeek.start && selectedWeek.end) {
            return `${formatDDMMYYYY(selectedWeek.start)} - ${formatDDMMYYYY(selectedWeek.end)}`;
        }
        if (period === 'today') return formatDDMMYYYY(new Date());
        return String(period || '');
    })();

    const ExportShareButtons = () => (
        <div className="button-group" style={{ display: 'flex', gap: '12px', margin: '16px 0' }}>
            <button 
                className="report-button" 
                onClick={() => setShowStockExportPicker(true)} 
                style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    backgroundColor: '#e9ecef',
                    color: '#000'
                }}
            >
                <FileText size={18} />Export Report
            </button>
            {/*
              REMOVED — the "Share Report" button.

              It rendered with cursor:pointer and carried no onClick at all, so tapping it
              did nothing. A control that looks live and is inert is worse than no control:
              the owner concludes sharing is broken rather than that it was never built.

              Export still covers the need (PDF/Excel through StockExportReportModal, which
              the user can then share by any means). To bring Share back properly it needs
              what the Sales Report screen has — build the PDF, hand it to navigator.share,
              fall back to a download when the browser cannot share files — which is a
              feature worth its own change, not a silent re-add of this button.
            */}
        </div>
    );


    const PerformanceAnalyticsCard = ({ data, isSelected, forwardedRef, onCardClick }) => {
        const [isFullscreen, setIsFullscreen] = useState(false);
        if (!data || !data.items || data.items.length === 0) {
            return (
                <div className={`${isSelected ? 'highlighted' : ''}`}>
                    <h3 className="card-title-external">Sales Performance Chart</h3>
                    <div className="card analytics-card">
                        <p style={{ textAlign: 'center', padding: '20px' }}>No performance data available.</p>
                    </div>
                </div>
            );
        }

        // Prepare data for flat donut (2D) with outside labels
        const chartData = data.items.map((item, index) => ({
            name: item.name,
            y: item.value,
            // Palette inspired by the screenshots (soft, readable colors)
            color: [
                '#ff8c1a', // Chrome (orange)
                '#6d28d9', // Safari (purple)
                '#a78bfa', // Samsung Internet (lavender)
                '#f472b6', // UC Browser (pink)
                '#ef4444', // Opera (red)
                '#f59e0b', // Others (amber)
                '#60a5fa', '#34d399', '#fbbf24', '#93c5fd', '#fca5a5', '#86efac'
            ][index % 12]
        }));

        // Calculate total for center display if needed
        const total = data.total || data.items.reduce((sum, item) => sum + item.value, 0);

        // Helper for INR formatting within chart scope
        const formatINR = (val) => {
            try {
                return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val || 0);
            } catch {
                return Highcharts.numberFormat(val || 0, 0);
            }
        };

        // Helper to wrap long labels nicely over 2-3 lines
        const wrapLabel = (txt, max = 16) => {
            if (!txt) return '';
            const words = String(txt).split(' ');
            const lines = [];
            let line = '';
            words.forEach(w => {
                const tryLine = line.length ? line + ' ' + w : w;
                if (tryLine.length > max) {
                    if (line) lines.push(line);
                    line = w;
                } else {
                    line = tryLine;
                }
            });
            if (line) lines.push(line);
            return lines.slice(0, 3).join('<br/>');
        };

        const openFullscreen = () => setIsFullscreen(true);
        const closeFullscreen = () => setIsFullscreen(false);

        // Make labels more readable around chart with wrapping and connectors
        const chartOptions = {
            chart: {
                type: 'pie',
                backgroundColor: 'transparent',
                height: 420,
                // Flat donut (no 3D)
                options3d: { enabled: false },
                events: {
                    click: openFullscreen
                }
            },
            title: {
                useHTML: true,
                text: `<div style="line-height:1.3; font-weight:600; color:#111827;">Total Sales<br/>₹${formatINR(total)}</div>`,
                align: 'center',
                verticalAlign: 'middle',
                y: 10,
                style: {
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937'
                }
            },
            tooltip: {
                backgroundColor: '#ffffff',
                borderColor: 'transparent',
                borderRadius: 10,
                borderWidth: 0,
                style: {
                    color: '#000000',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    opacity: 1
                },
                headerFormat: '<b>{point.key}</b><br/>',
                pointFormat: 'Sales: <b>₹{point.y:,.0f}</b>',
                shadow: { color: 'rgba(0, 0, 0, 0.15)', width: 8, offsetX: 0, offsetY: 4 }
            },
            legend: { enabled: false },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    innerSize: '60%',
                    size: '85%',
                    startAngle: 0,
                    endAngle: 360,
                    slicedOffset: 8,
                    dataLabels: {
                        enabled: true,
                        distance: 28,
                        softConnector: true,
                        connectorColor: '#d1d5db',
                        connectorWidth: 1.5,
                        connectorPadding: 6,
                        padding: 2,
                        crop: false,
                        overflow: 'allow',
                        useHTML: true,
                        formatter: function () {
                            const name = String(this.point.name || '');
                            const label = wrapLabel(name, 16);
                            return `<div style="max-width:140px;white-space:normal;line-height:1.15;color:#6b7280">${label}: ₹${formatINR(this.y)}</div>`;
                        },
                        style: {
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#6b7280',
                            textOutline: 'none'
                        },
                        allowOverlap: false
                    },
                    showInLegend: false,
                    point: {
                        events: {
                            mouseOver: function () {
                                // Gently pop out on hover
                                if (!this.sliced) this.slice(true, false);
                            },
                            mouseOut: function () {
                                if (this.sliced) this.slice(false, false);
                            },
                            click: function () { openFullscreen(); }
                        }
                    },
                    states: {
                        hover: {
                            brightness: 0.05,
                            halo: { size: 8, opacity: 0.15 }
                        },
                        select: {
                            color: null,
                            borderWidth: 3,
                            borderColor: '#f59e0b'
                        }
                    },
                    borderWidth: 1,
                    borderColor: '#ffffff'
                }
            },
            series: [{
                name: 'Sales (₹)',
                colorByPoint: true,
                data: chartData,
                animation: {
                    duration: 1200
                }
            }],
            credits: {
                enabled: false
            },
            responsive: {
                rules: [{
                    condition: {
                        maxWidth: 600
                    },
                    chartOptions: {
                        title: {
                            style: {
                                fontSize: '14px'
                            }
                        }
                    }
                }]
            }
        };

        return (
            <div ref={forwardedRef} className={`${isSelected ? 'highlighted' : ''}`}>
                <h3 className="card-title-external">Sales Performance Chart</h3>
                <div 
                    className="card analytics-card donut-card-flat clickable-card"
                    onClick={() => onCardClick && onCardClick('performance-analytics', data, 'Sales Performance Chart')}
                >
                    <div className="highcharts-container">
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={chartOptions}
                            containerProps={{ className: 'highcharts-chart' }}
                        />
                    </div>
                </div>
                {isFullscreen && (
                    <div className="chart-modal-overlay" onClick={closeFullscreen}>
                        <div className="chart-modal" onClick={(e) => e.stopPropagation()}>
                            <button className="chart-modal-close" onClick={closeFullscreen} aria-label="Close">✕</button>
                            <h3 className="chart-modal-title">Sales Performance</h3>
                            <div className="chart-modal-body">
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={{
                                        ...chartOptions,
                                        chart: {
                                            ...chartOptions.chart,
                                            height: (typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.72) : 560)
                                        },
                                        plotOptions: {
                                            ...chartOptions.plotOptions,
                                            pie: {
                                                ...chartOptions.plotOptions.pie,
                                                size: '88%',
                                                dataLabels: {
                                                    ...chartOptions.plotOptions.pie.dataLabels,
                                                    distance: 36,
                                                    formatter: function () {
                                                        const name = String(this.point.name || '');
                                                        const label = wrapLabel(name, 22);
                                                        return `<div style="max-width:220px;white-space:normal;line-height:1.2;color:#4b5563;font-size:13px">${label}: ₹${formatINR(this.y)}</div>`;
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                    containerProps={{ className: 'highcharts-chart fullscreen' }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const RawMaterialConsumptionCard = ({ data, isSelected, forwardedRef, onCardClick }) => {
        const [isFullscreen, setIsFullscreen] = useState(false);
        const chartContainerRef = useRef(null);
        const scrollTimeoutRef = useRef(null);

        // Handle scroll events to hide/show bars during scrolling
        useEffect(() => {
            const container = chartContainerRef.current;
            if (!container) return;

            const handleScroll = () => {
                // Add scrolling class to hide bars
                container.classList.add('scrolling');

                // Clear existing timeout
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }

                // Remove scrolling class after scroll ends
                scrollTimeoutRef.current = setTimeout(() => {
                    container.classList.remove('scrolling');
                }, 150);
            };

            container.addEventListener('scroll', handleScroll, { passive: true });

            return () => {
                container.removeEventListener('scroll', handleScroll);
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
            };
        }, []);

        if (!data || data.length === 0) {
            return (
                <div className={`${isSelected ? 'highlighted' : ''}`}>
                    <h3 className="card-title-external">Raw Material Stock Analysis</h3>
                    <div className="card consumption-card">
                        <div className="consumption-chart-container-new">
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '400px',
                                color: '#718096',
                                fontSize: '14px'
                            }}>
                                No consumption data available
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Transform data into multi-series format like the reference
        // Select top 6 items for better visualization
        const processedData = [...data]
            .slice(0, 6)
            .map(d => ({
                name: (d.name || 'Unknown Item').length > 12 ?
                    (d.name || 'Unknown Item').substring(0, 12) + '...' :
                    (d.name || 'Unknown Item'),
                fullName: d.name || 'Unknown Item',
                currentStock: Math.round(Number(d.totalStock || 0)),
                consumed: Math.round(Number(d.quantity || 0)),
                unit: d.unit || 'kg',
                consumption: Number(d.consumption || 0)
            }))
            .sort((a, b) => b.consumed - a.consumed);

        const categories = processedData.map(item => item.name);

        // Configure column chart options to match the reference style exactly
        const options = {
            chart: {
                type: 'column',
                backgroundColor: 'transparent',
                height: 380,
                spacing: [10, 10, 10, 10],
                margin: [50, 20, 60, 80], // Increased left margin for y-axis labels
                scrollablePlotArea: {
                    minWidth: Math.max(1400, processedData.length * 200), // Increased width for guaranteed scrolling
                    scrollPositionX: 1
                },
                events: {
                    click: () => setIsFullscreen(true)
                },
                plotBorderWidth: 0,
                style: {
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                },
                animation: {
                    duration: 1200,
                    easing: 'easeOutQuart'
                }
            },
            title: {
                text: null // Remove duplicate title since we have it in the card header
            },
            subtitle: {
                text: null // Remove subtitle to reduce clutter
            },
            credits: { enabled: false },
            exporting: { enabled: false },
            xAxis: {
                categories,
                title: { text: null },
                labels: {
                    rotation: 0, // Horizontal labels (0 degrees)
                    style: {
                        color: '#374151',
                        fontSize: '12px',
                        fontWeight: '500',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                    },
                    useHTML: false,
                    y: 15, // Fixed vertical position for all labels
                    align: 'center',
                    overflow: 'allow',
                    step: 1, // Show all labels
                    staggerLines: 0, // Disable staggering completely
                    maxStaggerLines: 1, // Prevent staggering
                    autoRotation: false, // Disable auto rotation
                    autoRotationLimit: 0 // No auto rotation limit
                },
                lineWidth: 2,
                lineColor: '#374151',
                tickLength: 6,
                tickWidth: 1,
                tickColor: '#374151',
                gridLineWidth: 0,
                offset: 15 // Increase offset to accommodate single-line labels
            },
            yAxis: {
                min: 0,
                allowDecimals: false,
                gridLineWidth: 1,
                gridLineColor: '#f3f4f6',
                lineWidth: 2,
                lineColor: '#374151',
                tickWidth: 2,
                tickLength: 8,
                tickColor: '#374151',
                startOnTick: true,
                endOnTick: true,
                title: {
                    text: 'Quantity (kg)',
                    style: {
                        color: '#374151',
                        fontSize: '13px',
                        fontWeight: '600',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                    },
                    margin: 15
                },
                labels: {
                    enabled: true,
                    style: {
                        color: '#374151',
                        fontSize: '12px',
                        fontWeight: '500',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                    },
                    x: -8,
                    y: 4,
                    formatter: function () {
                        const value = this.value;
                        if (value >= 1000) {
                            return (value / 1000) + 'K';
                        }
                        return value;
                    }
                }
            },
            legend: {
                enabled: true,
                align: 'center',
                verticalAlign: 'top',
                layout: 'horizontal',
                y: 10,
                itemStyle: {
                    color: '#374151',
                    fontSize: '12px',
                    fontWeight: '500',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                },
                symbolHeight: 12,
                symbolWidth: 16,
                symbolRadius: 2,
                itemDistance: 20,
                margin: 15,
                backgroundColor: 'transparent',
                borderWidth: 0,
                shadow: false
            },
            plotOptions: {
                column: {
                    borderWidth: 0,
                    borderRadius: 4,
                    pointPadding: 0.2, // Increased padding to prevent overlap with axis
                    groupPadding: 0.15, // Reduced group padding to minimize white space
                    dataLabels: {
                        enabled: true,
                        style: {
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#374151',
                            textOutline: 'none'
                        },
                        formatter: function () {
                            const value = this.y;
                            if (value >= 1000) {
                                return (value / 1000).toFixed(1) + 'K';
                            }
                            return value;
                        },
                        y: -10, // Position further above the bars
                        align: 'center',
                        allowOverlap: false,
                        crop: false,
                        overflow: 'allow'
                    },
                    states: {
                        hover: {
                            brightness: 0.1
                        },
                        inactive: {
                            opacity: 0.3 // Hide bars during scroll by making them semi-transparent
                        }
                    },
                    maxPointWidth: 60, // Reduced max width for better spacing
                    point: {
                        events: {
                            click: () => setIsFullscreen(true)
                        }
                    },
                    animation: {
                        duration: 1000,
                        easing: 'easeOutBounce'
                    }
                },
                series: {
                    animation: {
                        duration: 1200,
                        easing: 'easeOutQuart'
                    },
                    point: {
                        events: {
                            mouseOver: function () {
                                this.graphic.animate({
                                    scaleY: 1.05,
                                    scaleX: 1.02
                                }, {
                                    duration: 200,
                                    easing: 'easeOutQuad'
                                });
                            },
                            mouseOut: function () {
                                this.graphic.animate({
                                    scaleY: 1,
                                    scaleX: 1
                                }, {
                                    duration: 200,
                                    easing: 'easeOutQuad'
                                });
                            }
                        }
                    }
                }
            },
            series: [
                {
                    name: 'Current Stock',
                    data: processedData.map(item => item.currentStock),
                    color: '#f97316', // Bright orange color for better visibility
                    marker: { enabled: false },
                    animation: {
                        duration: 1000,
                        easing: 'easeOutBounce'
                    }
                },
                {
                    name: 'Consumed',
                    data: processedData.map(item => item.consumed),
                    color: '#8b5cf6', // Bright purple color for better visibility
                    marker: { enabled: false },
                    animation: {
                        duration: 1200,
                        easing: 'easeOutQuart'
                    }
                }
            ],
            tooltip: {
                useHTML: true,
                backgroundColor: 'transparent',
                borderWidth: 0,
                borderColor: 'transparent',
                borderRadius: 0,
                shadow: false,
                style: {
                    fontSize: '13px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    lineHeight: '1.4',
                    color: '#000000',
                    opacity: 1
                },
                distance: 20,
                outside: false,
                hideDelay: 500,
                opacity: 1,
                padding: 0,
                formatter: function () {
                    const pointIndex = this.point.index;
                    const item = processedData[pointIndex];
                    const percentage = item.currentStock > 0 ?
                        ((this.y / (item.currentStock + item.consumed)) * 100).toFixed(1) : 0;

                    return `<div style="background: #ffffff; padding: 10px 14px; min-width: 200px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);">` +
                           `<div style="font-weight: 600; color: #1f2937; margin-bottom: 8px; font-size: 14px;">${item.fullName || this.point.category}</div>` +
                           `<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">` +
                           `<span style="color: ${this.series.color}; font-weight: 600;">${this.series.name}:</span> ` +
                           `<span style="font-weight: 600; color: #1f2937;">${this.y.toLocaleString()} kg</span></div>` +
                           `<div style="color: #6b7280; font-size: 12px; text-align: center; margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb;">` +
                           `${percentage}% of total inventory</div></div>`;
                }
            },
            responsive: {
                rules: [{
                    condition: { maxWidth: 768 },
                    chartOptions: {
                        chart: {
                            height: 350,
                            scrollablePlotArea: {
                                minWidth: Math.max(1400, processedData.length * 200), // Preserve scrolling on mobile
                                scrollPositionX: 1
                            }
                        },
                        xAxis: {
                            labels: {
                                rotation: 0, // Keep horizontal even on mobile
                                style: { fontSize: '10px' },
                                staggerLines: 0, // No staggering on mobile either
                                maxStaggerLines: 1,
                                step: 1,
                                y: 15, // Same y position for consistency
                                autoRotation: false,
                                autoRotationLimit: 0
                            }
                        },
                        yAxis: {
                            labels: { style: { fontSize: '10px' } }
                        },
                        plotOptions: {
                            column: {
                                pointPadding: 0.2,
                                groupPadding: 0.15
                            }
                        }
                    }
                }]
            }
        };

        return (
            <div ref={forwardedRef} className={`${isSelected ? 'highlighted' : ''}`}>
                <h3 className="card-title-external">
                    Raw Material Stock vs Consumption
                    <span style={{
                        fontSize: '14px',
                        fontWeight: 'normal',
                        color: '#6b7280',
                        marginLeft: '8px'
                    }}>
                        (Top {processedData.length} items)
                    </span>
                </h3>
                <div 
                    className="card consumption-card clickable-card"
                    onClick={() => onCardClick && onCardClick('raw-material-consumption', data, 'Raw Material Consumption Report')}
                >
                    <div
                        ref={chartContainerRef}
                        className="consumption-chart-container-new"
                    >
                        <div style={{
                            minWidth: `${Math.max(1400, processedData.length * 200)}px`,
                            width: `${Math.max(1400, processedData.length * 200)}px`,
                            height: '380px'
                        }}>
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={options}
                                containerProps={{
                                    className: 'highcharts-chart consumption-chart'
                                }}
                            />
                        </div>
                    </div>
                </div>
                {isFullscreen && (
                    <div className="fullscreen-modal" onClick={() => setIsFullscreen(false)}>
                        <div className="fullscreen-modal-content" onClick={(e) => e.stopPropagation()}>
                            <button className="fullscreen-close-btn" onClick={() => setIsFullscreen(false)} aria-label="Close">×</button>
                            <h3 style={{
                                margin: '0 0 30px 0',
                                fontSize: '24px',
                                fontWeight: '600',
                                color: '#1f2937',
                                textAlign: 'center'
                            }}>
                                Raw Material Stock vs Consumption Analysis
                            </h3>
                            <div style={{
                                height: 'calc(95vh - 120px)',
                                width: '100%',
                                padding: '0 20px',
                                backgroundColor: '#ffffff',
                                borderRadius: '12px',
                                overflowX: 'auto',
                                overflowY: 'hidden'
                            }}>
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={{
                                        ...options,
                                        chart: {
                                            ...options.chart,
                                            height: 'calc(95vh - 180px)',
                                            events: {},
                                            scrollablePlotArea: {
                                                minWidth: 1200
                                            }
                                        },
                                        title: {
                                            ...options.title,
                                            text: null
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const WastageComparisonCard = ({ data, isSelected, forwardedRef, onCardClick }) => {
        if (!data) return null;

        // Currency formatter (INR)
        const formatINR = (v) => {
            try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v || 0)); }
            catch { return `₹${(Number(v || 0)).toLocaleString('en-IN')}`; }
        };

        // Shorten long item names for the x-axis (full name appears in tooltips)
        const shorten = (label) => {
            if (!label) return '';
            const trimmed = String(label).trim();
            return trimmed.length > 14 ? `${trimmed.slice(0, 14)}…` : trimmed;
        };

        // Lightweight crosshair plugin (vertical tracker)
        const crosshairPlugin = {
            id: 'wastageCrosshair',
            afterDatasetsDraw(chart, args, pluginOptions) {
                const tooltip = chart.tooltip;
                const active = tooltip?.getActiveElements?.();
                if (!active || !active.length) return;
                const ctx = chart.ctx;
                const x = active[0].element.x;
                const { top, bottom } = chart.chartArea;
                ctx.save();
                ctx.strokeStyle = pluginOptions?.color || '#9ca3af';
                ctx.lineWidth = pluginOptions?.lineWidth || 1;
                ctx.setLineDash(pluginOptions?.dash || [5, 5]);
                ctx.beginPath();
                ctx.moveTo(x, top);
                ctx.lineTo(x, bottom);
                ctx.stroke();
                ctx.restore();
            }
        };

        const chartData = {
            labels: data.labels,
            datasets: [{
                label: 'Wastage (₹)',
                data: data.data,
                borderColor: '#3b82f6',
                borderWidth: 3,
                // Dynamic vertical gradient fill
                backgroundColor: (context) => {
                    const { chart } = context;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return 'rgba(59,130,246,0.2)';
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(59,130,246,0.35)');
                    gradient.addColorStop(1, 'rgba(59,130,246,0.05)');
                    return gradient;
                },
                fill: true,
                tension: 0.35,
                pointBackgroundColor: '#ffffff',
                pointBorderColor: '#3b82f6',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 7,
                pointHitRadius: 12,
                clip: 10,
            }],
        };

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { right: 12, left: 8, top: 10, bottom: 0 } },
            interaction: { intersect: false, mode: 'index', axis: 'x' },
            animation: { duration: 800, easing: 'easeOutCubic' },
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: true,
                    backgroundColor: '#ffffff',
                    titleColor: '#000000',
                    bodyColor: '#374151',
                    borderColor: 'transparent',
                    borderWidth: 0,
                    padding: 12,
                    displayColors: false,
                    callbacks: {
                        title: (items) => (items?.[0]?.label ?? ''),
                        label: (ctx) => {
                            const value = ctx.parsed.y;
                            // Delta against previous point
                            const prev = ctx.dataIndex > 0 ? ctx.dataset.data[ctx.dataIndex - 1] : null;
                            const delta = prev != null ? (value - prev) : null;
                            const deltaStr = delta == null ? '' : `  (${delta >= 0 ? '+' : ''}${formatINR(delta)})`;
                            return `Wastage: ${formatINR(value)}${deltaStr}`;
                        }
                    }
                },
                wastageCrosshair: { color: '#94a3b8', lineWidth: 1, dash: [4, 4] }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    grid: { color: '#eef2ff' },
                    ticks: {
                        color: '#6b7280',
                        callback: (value) => formatINR(value)
                    },
                    border: { display: false }
                },
                x: {
                    grid: { display: false },
                    ticks: {
                        color: '#374151',
                        maxRotation: 45,
                        minRotation: 45,
                        callback: (val, idx) => shorten(data.labels[idx])
                    },
                    border: { display: false }
                }
            },
            onHover: (evt, activeEls) => {
                evt.native && (evt.native.target.style.cursor = activeEls.length ? 'pointer' : 'default');
            },
            // Click to focus a point (pin tooltip)
            onClick: (evt, activeEls, chart) => {
                if (!activeEls?.length) return;
                chart.setActiveElements(activeEls);
                chart.tooltip?.setActiveElements?.(activeEls, { x: evt.x, y: evt.y });
                chart.update();
            }
        };

        return (
            <div ref={forwardedRef} className={`${isSelected ? 'highlighted' : ''}`}>
                <h3 className="card-title-external">Wastage Comparison (In ₹)</h3>
                <div 
                    className="card wastage-card clickable-card"
                    onClick={() => onCardClick && onCardClick('wastage-comparison', data, 'Wastage Comparison (in ₹)')}
                >
                    <span className="see-all-link">See All</span>
                    <div className="line-graph-container">
                        <Line options={chartOptions} data={chartData} plugins={[crosshairPlugin]} />
                    </div>
                </div>
            </div>
        );
    };

    const EstimatedVsRealtimeCard = ({ data, period, isSelected, forwardedRef, onCardClick, onOpenEstimatedGraph, animationDelay = 0 }) => {
        const [menuOpen, setMenuOpen] = useState(false);
        const [isCategoryOpen, setIsCategoryOpen] = useState(false);
        const menuRef = useRef(null);
        const categoryRef = useRef(null);

        useEffect(() => {
            if (!menuOpen && !isCategoryOpen) return undefined;
            const handleOutsideClick = (e) => {
                if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
                    setMenuOpen(false);
                }
                if (isCategoryOpen && categoryRef.current && !categoryRef.current.contains(e.target)) {
                    setIsCategoryOpen(false);
                }
            };
            document.addEventListener('mousedown', handleOutsideClick);
            document.addEventListener('touchstart', handleOutsideClick);
            return () => {
                document.removeEventListener('mousedown', handleOutsideClick);
                document.removeEventListener('touchstart', handleOutsideClick);
            };
        }, [menuOpen, isCategoryOpen]);

        // For consistent styling / sorting we derive a sorted copy of data
        const sortedData = useMemo(() => {
            if (!Array.isArray(data)) return [];
            // actual_plates might be string; ensure numeric comparison
            return [...data].sort((a,b) => Number(b.actual_plates||0) - Number(a.actual_plates||0));
        }, [data]);
        const selectedCat = useMemo(() => {
            try {
                return (menuCategories || []).find(c => String(c.category_id) === String(selectedCategory));
            } catch {
                return null;
            }
        }, [menuCategories, selectedCategory]);
        
        const hasData = data && Array.isArray(data) && data.length > 0;

        // Table-based display for Estimated vs Actual Plates
        return (
            <div ref={forwardedRef} className={`${isSelected ? 'highlighted' : ''} report-enter-card`} style={getEnterDelayStyle(animationDelay)}>
                <div className="card estimated-realtime-card" style={{ justifyContent: 'flex-start' }}>
                    {/* Header with title on left, filter and hamburger icons on right */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <h3 className="card-title-external" style={{ paddingLeft:'0rem', marginTop: 0, marginBottom: 0, textAlign: 'left' }}>{t('reports.stock.usage.title')}</h3>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Filter Icon */}
                            <div ref={categoryRef} style={{ position: 'relative' }}>
                                <button
                                    type="button"
                                    aria-label="Filter by category"
                                    onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: 36,
                                    width: 36,
                                    borderRadius: 6,
                                    border: '1px solid #d1d5db',
                                    backgroundColor: '#ffffff',
                                    cursor: 'pointer'
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                                </svg>
                            </button>
                            {isCategoryOpen && (
                                <div style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 44,
                                    background: '#ffffff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                                    minWidth: 200,
                                    zIndex: 10,
                                    maxHeight: '300px',
                                    overflowY: 'auto'
                                }}>
                                    <div 
                                        onClick={() => { setSelectedCategory(null); setIsCategoryOpen(false); }}
                                        style={{
                                            padding: '10px 12px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #f3f4f6',
                                            fontSize: '14px',
                                            color: '#111827',
                                            fontWeight: selectedCategory === null ? '600' : '400'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        All Categories
                                    </div>
                                    {(menuCategories || []).map(cat => (
                                        <div 
                                            key={cat.category_id}
                                            onClick={() => { setSelectedCategory(cat.category_id); setIsCategoryOpen(false); }}
                                            style={{
                                                padding: '10px 12px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f3f4f6',
                                                fontSize: '14px',
                                                color: '#111827',
                                                fontWeight: String(selectedCategory) === String(cat.category_id) ? '600' : '400',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                gap: '12px'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <div>{cat.category_name}</div>
                                            <div style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{cat.item_count} items</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Hamburger Icon */}
                        <div ref={menuRef} style={{ position: 'relative' }}>
                            <button
                                type="button"
                                aria-label="More actions"
                                onClick={() => setMenuOpen(!menuOpen)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: 36,
                                    width: 36,
                                    borderRadius: 6,
                                    border: '1px solid #d1d5db',
                                    backgroundColor: '#ffffff',
                                    cursor: 'pointer'
                                }}
                            >
                                <Menu size={18} />
                            </button>
                            {menuOpen && (
                                <div style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 44,
                                    background: '#ffffff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                                    minWidth: 160,
                                    zIndex: 10
                                }}>
                                    <button
                                        type="button"
                                        onClick={() => { setMenuOpen(false); onOpenEstimatedGraph && onOpenEstimatedGraph(); }}
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            padding: '10px 12px',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            color: '#111827'
                                        }}
                                    >
                                        View Graph
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="card-content-scrollable" style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                {!hasData || sortedData.length === 0 ? (
                    <NoDataPlaceholder
                        type="usage"
                        title="No usage data yet"
                        subtitle="Compare expected vs actual consumption once items start moving."
                        minHeight="100%"
                        style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
                    />
                ) : (
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto', marginTop: '6px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f9fafb', zIndex: 1 }}>
                                <tr>
                                    <th style={{ 
                                        padding: '12px 16px', 
                                        textAlign: 'left', 
                                        borderBottom: '2px solid #e5e7eb', 
                                        fontWeight: '600',
                                        color: '#000000'
                                    }}>{t('reports.stock.usage.menuItem')}</th>
                                    <th style={{ 
                                        padding: '12px 16px', 
                                        textAlign: 'center', 
                                        borderBottom: '2px solid #e5e7eb', 
                                        fontWeight: '600',
                                        color: '#000000'
                                    }}>{t('reports.stock.usage.estimated')}</th>
                                    <th style={{ 
                                        padding: '12px 16px', 
                                        textAlign: 'center', 
                                        borderBottom: '2px solid #e5e7eb', 
                                        fontWeight: '600',
                                        color: '#000000'
                                    }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((item, index) => {
                                    const actualPlates = parseFloat(item.actual_plates) || 0;
                                    const wastagePlates = parseFloat(item.wasted_plates) || 0;
                                    const totalPlates = actualPlates + wastagePlates;
                                    
                                    return (
                                        <tr key={index} className="report-enter-item" style={{
                                            ...getStaggerDelayStyle(index, 120, 28, 320),
                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                            transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                        >
                                            <td style={{ 
                                                padding: '12px 16px', 
                                                borderBottom: '1px solid #e5e7eb',
                                                color: '#374151',
                                                fontWeight: '500',
                                                textAlign: 'left'
                                            }}>{item.menu_item}</td>
                                            <td style={{ 
                                                padding: '12px 16px', 
                                                textAlign: 'center', 
                                                borderBottom: '1px solid #e5e7eb',
                                                color: 'orange',
                                                fontWeight: 600
                                            }}>{item.estimated}</td>
                                            <td style={{ 
                                                padding: '12px 16px', 
                                                textAlign: 'center', 
                                                borderBottom: '1px solid #e5e7eb',
                                                fontWeight: 700,
                                                fontSize: '16px',
                                                color: '#22c55e'
                                            }}>
                                                {totalPlates}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                </div>
                </div>
            </div>
        );
    };

    // Weekly Trend Graph Component
    const WeeklyTrendGraph = () => {
        if (!selectedWeek || !selectedWeek.isWeekSelection || weeklyTrend.length === 0) return null;

        const chartOptions = {
            chart: {
                type: 'column',
                backgroundColor: 'transparent',
                height: 350,
                marginLeft: window.innerWidth <= 768 ? 60 : 80,
                marginRight: window.innerWidth <= 768 ? 10 : 20
            },
            title: {
                text: `Weekly Sales: ${formatWeekDisplay(selectedWeek)}`,
                style: { fontSize: '16px', fontWeight: '600', color: '#333' }
            },
            xAxis: {
                categories: weeklyTrend.map(d => d.label),
                labels: { style: { fontSize: '12px' } }
            },
            yAxis: {
                title: { text: 'Sales Amount (₹)', style: { fontSize: '12px' } },
                labels: {
                    formatter: function() {
                        return '₹' + Highcharts.numberFormat(this.value, 0);
                    }
                }
            },
            legend: { enabled: false },
            tooltip: {
                backgroundColor: '#ffffff',
                borderColor: 'transparent',
                borderRadius: 8,
                borderWidth: 0,
                style: {
                    color: '#000000',
                    fontSize: '13px',
                    opacity: 1
                },
                shadow: { color: 'rgba(0, 0, 0, 0.15)', width: 8, offsetX: 0, offsetY: 4 },
                formatter: function() {
                    const dayData = weeklyTrend[this.point.index];
                    // null means at least one item sold that day could not be costed. Showing
                    // it as ₹0 would read as "no profit" rather than "not known".
                    const gp = dayData.gross_profit;
                    const gpText = (gp === null || gp === undefined)
                        ? '-'
                        : `₹${Highcharts.numberFormat(gp, 0)}`;
                    return `<b>${this.x} (${dayData.date})</b><br/>` +
                           `Total Sales: ₹${Highcharts.numberFormat(this.y, 0)}<br/>` +
                           `Gross Profit: ${gpText}`;
                }
            },
            plotOptions: {
                column: {
                    colorByPoint: true,
                    colors: weeklyTrend.map((d, i) => {
                        const sales = weeklyTrend.map(x => x.total_sales);
                        const minSales = Math.min(...sales.filter(s => s > 0));
                        const maxSales = Math.max(...sales);
                        if (d.total_sales === 0) return '#93c5fd'; // Light blue for zero
                        if (d.total_sales === maxSales && maxSales > 0) return '#1e40af'; // Dark blue for highest
                        if (d.total_sales === minSales && minSales > 0) return '#60a5fa'; // Medium blue for lowest
                        return '#2563eb'; // Default blue for others
                    }),
                    dataLabels: {
                        enabled: true,
                        formatter: function() {
                            return '₹' + Highcharts.numberFormat(this.y, 0);
                        },
                        style: { fontSize: '11px', fontWeight: '600' }
                    }
                }
            },
            series: [{
                name: 'Sales',
                data: weeklyTrend.map(d => d.total_sales)
            }],
            credits: { enabled: false },
            exporting: { enabled: false }
        };

        return (
            <div style={{
                margin: '20px 0',
                padding: window.innerWidth <= 768 ? '15px 10px' : '20px',
                backgroundColor: '#fff',
                borderRadius: '12px',
                boxShadow: window.innerWidth <= 768 ? '0 6px 20px rgba(0,0,0,0.15)' : '0 4px 16px rgba(0,0,0,0.12)'
            }}>
                <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                />
            </div>
        );
    };


    if (loading) return <AppLoader />;
    if (error) return <div className="error-message">Error: {error}</div>;

    return (
        <>
            {showAutoModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '10px'
                    }}
                    onClick={() => setShowAutoModal(false)}
                >
                    <div
                        style={{
                            width: 'min(420px, 92vw)',
                            background: '#fff',
                            borderRadius: 12,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            padding: '28px 22px',
                            textAlign: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
                            <div
                                style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '50%',
                                    border: '3px solid #ef4444',
                                    backgroundColor: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '28px',
                                    fontWeight: '800',
                                    color: '#ef4444',
                                    lineHeight: 1
                                }}
                            >
                                !
                            </div>
                        </div>

                        <div
                            style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                marginBottom: '8px',
                                color: '#1f2937'
                            }}
                        >
                            Unable to load report
                        </div>

                        <div
                            style={{
                                fontSize: '14px',
                                color: '#374151',
                                whiteSpace: 'pre-line'
                            }}
                        >
                            {autoModalMessage}
                        </div>
                    </div>
                </div>
            )}
            <div className={`screen-content stockrepo-container ${isExporting ? 'capture-mode' : ''}`} ref={containerRef}>
                <div className="main-content-area">
                    <div className="stock-sticky">
                        <header className="stk-top-header">
                            <div className="stk-top-header-row">
                                <button
                                    type="button"
                                    aria-label="Back"
                                    onClick={onBack}
                                    className="stk-top-back-btn"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </button>
                                <h1 className="stk-top-title">Stock Report</h1>
                            </div>
                        </header>
                    </div>
                    <div className="stk-top-date-selector">
                        <DateSelector period={period} setPeriod={setPeriod} customDate={customDate} setCustomDate={setCustomDate} />
                    </div>
                    <WeekNavigator />
                    {/* Display Total Sales and Gross Profit Summary */}
                    {/*
                    {headerData && (
                        <div className="summary-boxes" style={{ marginBottom: '20px' }}>
                            <div className="summary-box">
                                <p className="summary-label">Total Sales</p>
                                <p className="summary-value">₹{headerData.totalSales}</p>
                                <p className="summary-change">
                                    <ArrowUpRight size={14} className="summary-change-icon" />
                                    {headerData.change}
                                </p>
                            </div>
                            <div className="summary-box">
                                <p className="summary-label">Gross Profit</p>
                                <p className="summary-value">₹{headerData.grossProfit}</p>
                                <p className="summary-change">
                                    <ArrowUpRight size={14} className="summary-change-icon" />
                                    {headerData.change}
                                </p>
                            </div>
                            <div className="summary-box">
                                <p className="summary-label">Items Sold</p>
                                <p className="summary-value">{headerData.itemsSold}</p>
                            </div>
                        </div>
                    )}
                    */}
                    {/* Weekly trend graph removed per request */}
                    <ItemWiseSalesCard 
                        data={itemWiseSalesData} 
                        isSelected={selectedReportId === 'stock-itemwise'} 
                        forwardedRef={el => reportRefs.current['stock-itemwise'] = el}
                        onCardClick={handleGraphCardClick}
                        onOpenGraph={handleOpenItemWiseGraph}
                        animationDelay={60}
                    />
                    <RawMaterialStockCard 
                        data={rawMaterialStockData} 
                        isSelected={selectedReportId === 'stock-performance'} 
                        forwardedRef={el => reportRefs.current['stock-performance'] = el}
                        onCardClick={handleGraphCardClick}
                        onOpenGraph={handleOpenRawMaterialGraph}
                        animationDelay={140}
                    />
                    <EstimatedVsRealtimeCard 
                        data={estimatedRealtimeData} 
                        period={period}
                        isSelected={selectedReportId === 'stock-consumption'} 
                        forwardedRef={el => reportRefs.current['stock-consumption'] = el}
                        onCardClick={handleGraphCardClick}
                        onOpenEstimatedGraph={handleOpenEstimatedVsActualGraph}
                        animationDelay={220}
                    />
                    <ExportShareButtons />
                    
                    {/* Inline Pickup section removed; graph opens in fullscreen popup now */}
                </div>
            </div>

            {/* Stock Export Picker Modal */}
            <StockExportReportModal
                isOpen={showStockExportPicker}
                onClose={() => setShowStockExportPicker(false)}
                itemWiseSalesData={itemWiseSalesData}
                rawMaterialStockData={rawMaterialStockData}
                estimatedRealtimeData={Array.isArray(estimatedRealtimeData) ? estimatedRealtimeData : []}
                periodLabel={exportPeriodLabel}
            />
            {showExportSuccess && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '10px'
                }} onClick={() => setShowExportSuccess(false)}>
                    <div style={{
                        width: 'min(380px, 90vw)',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        padding: '32px 24px',
                        textAlign: 'center'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                border: '3px solid #22c55e',
                                backgroundColor: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Check size={36} color="#22c55e" strokeWidth={3} />
                            </div>
                        </div>
                        <div style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '16px',
                            color: '#1f2937'
                        }}>
                            Report Downloaded Successfully
                        </div>
                        <button
                            onClick={() => setShowExportSuccess(false)}
                            style={{
                                padding: '10px 24px',
                                backgroundColor: '#000000',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* Graph Popup (clean body) */}
            <GraphPopup
                isOpen={!!selectedGraphPopup && selectedGraphPopup !== 'pickup-report'}
                onClose={closeGraphPopup}
                title={selectedGraphPopup === 'item-wise-sales' ? t('reports.stock.itemWise.title') :
                       selectedGraphPopup === 'raw-material-stock' ? t('reports.rawMaterialStockReport') :
                       selectedGraphPopup === 'estimated-realtime' ? t('reports.stock.usage.title') : ''}
                extraClass={selectedGraphPopup === 'item-wise-sales' ? 'item-wise-sales-popup' : ''}
            >
                {selectedGraphPopup === 'item-wise-sales' && popupData && Array.isArray(popupData) && popupData.length > 0 && (
                    <div className="item-wise-sales-modal-container">
                        {/* Search Bar */}
                        <div style={{ 
                            position: 'sticky',
                            top: '-16px',
                            background: '#fff',
                            zIndex: 100,
                            paddingTop: '16px',
                            paddingBottom: '16px',
                            marginLeft: '-20px',
                            marginRight: '-20px',
                            paddingLeft: '20px',
                            paddingRight: '20px',
                            marginTop: '-16px',
                            marginBottom: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}>
                            <StandardSearch
                                value={modalSearch}
                                onChange={setModalSearch}
                                onClear={() => setModalSearch('')}
                                placeholder="Search items"
                                showClearButton={true}
                                style={{ marginBottom: '0' }}
                            />
                        </div>
                        
                        {filteredPopupData.length === 0 ? (
                            modalSearch.trim() ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '300px',
                                    color: '#999',
                                    fontSize: '15px',
                                    textAlign: 'center',
                                    padding: '40px 20px'
                                }}>
                                    No items match your search
                                </div>
                            ) : (
                                <NoDataPlaceholder
                                    type="sales"
                                    title="No item sales yet"
                                    subtitle="A breakdown of sales by item appears here after your first sale."
                                    minHeight="260px"
                                />
                            )
                        ) : (
                        filteredPopupData.map((item, index) => (
                            <SalesItemRow
                                key={item.name || index}
                                item={item}
                                isLast={index === filteredPopupData.length - 1}
                                modalSearch={modalSearch}
                                anchorDate={reportAnchorDate}
                                language={language}
                            />
                        ))
                        )}
                    </div>
                )}
                {selectedGraphPopup === 'item-wise-sales' && (!popupData || !Array.isArray(popupData) || popupData.length === 0) && (
                    <NoDataPlaceholder
                        type="sales"
                        title="No item sales yet"
                        subtitle="A breakdown of sales by item appears here after your first sale."
                        minHeight="320px"
                    />
                )}
                {selectedGraphPopup === 'raw-material-stock' && popupData && Array.isArray(popupData) && popupData.length > 0 && (
                    <div className="raw-material-modal-container">
                        {/* ── 4 Financial Header Cards ── */}
                        {inventoryValuation && (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '10px',
                                marginBottom: '20px'
                            }}>
                                {/* Card 1: Opening Inventory Value */}
                                <div style={{
                                    background: '#ffffff',
                                    borderRadius: '12px',
                                    padding: '14px 12px',
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                }}>
                                    <div style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280', marginBottom: '6px', letterSpacing: '0.3px' }}>Cumulative Opening Value</div>
                                    <div style={{ fontSize: '20px', fontWeight: '700', lineHeight: 1.2, color: '#111827' }}>
                                        {`₹${Math.round(inventoryValuation.openingValue).toLocaleString('en-IN')}`}
                                    </div>
                                    {inventoryValuation.hasNegativeStock && (
                                        <div style={{ fontSize: '9px', color: '#ef4444', marginTop: '4px' }}>⚠ Approximate (negative stock items exist)</div>
                                    )}
                                </div>
                                {/* Card 2: Total Purchase Value */}
                                <div style={{
                                    background: '#ffffff',
                                    borderRadius: '12px',
                                    padding: '14px 12px',
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                }}>
                                    <div style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280', marginBottom: '6px', letterSpacing: '0.3px' }}>Total Stock Inflow (Bills)</div>
                                    <div style={{ fontSize: '20px', fontWeight: '700', lineHeight: 1.2, color: '#111827' }}>
                                        ₹{Math.round(inventoryValuation.purchaseValue).toLocaleString('en-IN')}
                                    </div>
                                </div>
                                {/* Card 3: Closing Inventory Value */}
                                <div style={{
                                    background: '#ffffff',
                                    borderRadius: '12px',
                                    padding: '14px 12px',
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                }}>
                                    <div style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280', marginBottom: '6px', letterSpacing: '0.3px' }}>Cumulative Closing Value</div>
                                    <div style={{ fontSize: '20px', fontWeight: '700', lineHeight: 1.2, color: '#111827' }}>
                                        {`₹${Math.round(inventoryValuation.closingValue).toLocaleString('en-IN')}`}
                                    </div>
                                    {inventoryValuation.hasNegativeStock && (
                                        <div style={{ fontSize: '9px', color: '#ef4444', marginTop: '4px' }}>⚠ Approximate (negative stock items exist)</div>
                                    )}
                                </div>
                                {/* Card 4: Total Consumption Value (COGS) */}
                                <div style={{
                                    background: '#ffffff',
                                    borderRadius: '12px',
                                    padding: '14px 12px',
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                }}>
                                    <div style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280', marginBottom: '6px', letterSpacing: '0.3px' }}>Total Inventory Consumed</div>
                                    <div style={{ fontSize: '20px', fontWeight: '700', lineHeight: 1.2, color: '#111827' }}>
                                        {`₹${Math.round(inventoryValuation.consumptionValue).toLocaleString('en-IN')}`}
                                    </div>
                                    {inventoryValuation.wastageCost > 0 && (
                                        <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '4px' }}>
                                            Includes ₹{Math.round(inventoryValuation.wastageCost).toLocaleString('en-IN')} reported wastage
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── Search Bar ── */}
                        <div style={{ 
                            position: 'sticky',
                            top: '-16px',
                            background: '#fff',
                            zIndex: 100,
                            paddingTop: '16px',
                            paddingBottom: '16px',
                            marginTop: '-16px',
                            marginBottom: '4px',
                            marginLeft: '-20px',
                            marginRight: '-20px',
                            paddingLeft: '20px',
                            paddingRight: '20px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}>
                            <StandardSearch
                                value={rawMaterialSearch}
                                onChange={setRawMaterialSearch}
                                onClear={() => setRawMaterialSearch('')}
                                placeholder="Search materials"
                                showClearButton={true}
                                style={{ marginBottom: '0' }}
                            />
                        </div>

                        {/* ── Ingredient List (Original Flat Cards) ── */}
                        {filteredRawMaterialData.length === 0 ? (
                            rawMaterialSearch.trim() ? (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '200px',
                                    color: '#999',
                                    fontSize: '15px',
                                    textAlign: 'center',
                                    padding: '40px 20px'
                                }}>
                                    No materials match your search
                                </div>
                            ) : (
                                <NoDataPlaceholder
                                    type="stock"
                                    title="No stock data yet"
                                    subtitle="Add stock-in items or inventory count to view raw material stock."
                                    minHeight="220px"
                                />
                            )
                        ) : (
                        filteredRawMaterialData.map((item, index) => (
                            <div 
                                key={index} 
                                className="raw-material-item-card"
                                style={index === filteredRawMaterialData.length - 1 ? { marginBottom: '8px' } : {}}
                            >
                                {/* Item Header - Name and Status */}
                                <div className="raw-material-item-header">
                                    <h4 className="raw-material-item-name">
                                        {highlightMatch(item.name, rawMaterialSearch)}
                                    </h4>
                                    <div className={`raw-material-status-badge ${
                                        item.status === 'Sufficient' ? 'sufficient' : 
                                        item.status === 'Low Stock' ? 'low-stock' : 
                                        item.status === 'Out of Stock' ? 'out-of-stock' : 'default'
                                    }`}>
                                        {item.status}
                                    </div>
                                </div>
                                
                                {/* Stock Details - Single Row (Horizontal) */}
                                <div className="raw-material-stock-grid">
                                    <div>
                                        <div className="raw-material-stock-label">Before</div>
                                        <div className="raw-material-stock-value before">
                                            {item.before} {item.beforeUnit}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="raw-material-stock-label">Deducted</div>
                                        <div className="raw-material-stock-value deducted">
                                            {item.deducted === '-' ? '-' : `${item.deducted} ${item.deductedUnit}`}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="raw-material-stock-label">Wasted</div>
                                        <div className="raw-material-stock-value wasted">
                                            {item.wasted} {item.wastedUnit}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="raw-material-stock-label">After</div>
                                        <div className="raw-material-stock-value after">
                                            {item.after} {item.afterUnit}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                        )}
                    </div>
                )}
                {selectedGraphPopup === 'estimated-realtime' && popupData && Array.isArray(popupData) && popupData.length > 0 && (
                    <div style={{ 
                        padding: '20px', 
                        width: '100%', 
                        height: 'auto',
                        maxHeight: 'calc(100vh - 150px)', 
                        overflowX: 'auto', 
                        overflowY: 'visible',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{ 
                            minWidth: `${Math.max(600, popupData.length * 100)}px`, 
                            height: 'auto',
                            flex: '0 0 auto'
                        }}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={{
                                chart: {
                                    type: 'column',
                                    height: window.innerHeight < 700 ? 320 : 380,
                                    backgroundColor: 'transparent',
                                    width: Math.max(600, popupData.length * 100),
                                    marginBottom: 105
                                },
                                title: {
                                    text: null
                                },
                                xAxis: {
                                    categories: popupData.map(item => item.menu_item || 'Unknown'),
                                    title: {
                                        text: null
                                    },
                                    labels: {
                                        rotation: 0,
                                        style: {
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            width: '90px',
                                            whiteSpace: 'normal'
                                        },
                                        useHTML: true,
                                        formatter: function() {
                                            return '<div style="width:90px;text-align:center;word-wrap:break-word;line-height:1.2;">' + this.value + '</div>';
                                        }
                                    }
                                },
                                yAxis: {
                                    min: 0,
                                    title: {
                                        text: 'Plates',
                                        style: {
                                            fontSize: '13px',
                                            fontWeight: '600'
                                        }
                                    },
                                    labels: {
                                        style: {
                                            fontSize: '12px'
                                        }
                                    },
                                    stackLabels: {
                                        enabled: true,
                                        style: {
                                            fontSize: '13px',
                                            fontWeight: 'bold',
                                            color: '#000',
                                            textOutline: '2px white'
                                        },
                                        verticalAlign: 'top',
                                        y: -5,
                                        formatter: function() {
                                            // Only show total for the 'actual' stack (green + red)
                                            if (this.stack === 'actual') {
                                                return this.total > 0 ? this.total : '';
                                            }
                                            return '';
                                        }
                                    }
                                },
                                tooltip: {
                                    shared: true,
                                    backgroundColor: 'transparent',
                                    borderColor: 'transparent',
                                    borderWidth: 0,
                                    borderRadius: 0,
                                    shadow: false,
                                    useHTML: true,
                                    padding: 0,
                                    opacity: 1,
                                    style: {
                                        fontSize: '12px',
                                        color: '#000',
                                        fontWeight: '500',
                                        backgroundColor: 'transparent',
                                        opacity: 1,
                                        whiteSpace: 'nowrap'
                                    },
                                    positioner: function(labelWidth, labelHeight, point) {
                                        const chartWidth = (this.chart && this.chart.chartWidth) ? this.chart.chartWidth : 600;
                                        const hoverPoints = this.chart && this.chart.hoverPoints;
                                        
                                        // Find the leftmost and rightmost X coordinates of the category
                                        const minPlotX = (hoverPoints && hoverPoints.length > 0) 
                                            ? Math.min(...hoverPoints.map(p => p.plotX)) 
                                            : point.plotX;
                                        const maxPlotX = (hoverPoints && hoverPoints.length > 0) 
                                            ? Math.max(...hoverPoints.map(p => p.plotX)) 
                                            : point.plotX;
                                            
                                        // Default positioning: to the right of the category
                                        let tooltipX = maxPlotX + 15;
                                        
                                        // If it would overflow the right edge of the chart:
                                        if (tooltipX + labelWidth > chartWidth) {
                                            tooltipX = Math.max(10, minPlotX - labelWidth - 15);
                                        }
                                        
                                        return {
                                            x: tooltipX,
                                            y: Math.max(10, point.plotY - labelHeight / 2)
                                        };
                                    },
                                    formatter: function() {
                                        const point = this.points[0];
                                        const item = popupData[point.point.index];
                                        const actualPlates = parseFloat(item.actual_plates) || 0;
                                        const wastagePlates = parseFloat(item.wasted_plates) || 0;
                                        const totalPlates = actualPlates + wastagePlates;
                                        const estimatedPlates = parseFloat(item.estimated) || 0;
                                        const menuItemName = String(item.menu_item || 'Unknown');
                                        
                                        return `<div style="background: #ffffff; padding: 12px 16px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); white-space: nowrap; line-height: 1.6;">` +
                                               `<b style="color: #000; font-size: 13px; display: block; margin-bottom: 4px;">${menuItemName}</b>` +
                                               `<span style="color: #ff9800; font-size: 12px; display: block; margin-bottom: 1px;">● Estimated: <b>${estimatedPlates}</b> plates</span>` +
                                               `<span style="color: #22c55e; font-size: 12px; display: block; margin-bottom: 1px;">● Actual Sold: <b>${actualPlates}</b> plates</span>` +
                                               `<span style="color: #ef4444; font-size: 12px; display: block; margin-bottom: 4px;">● Wastage: <b>${wastagePlates}</b> plates</span>` +
                                               `<b style="color: #555; font-size: 12px; display: block;">Total: ${totalPlates} plates</b></div>`;
                                    }
                                },
                                plotOptions: {
                                    column: {
                                        stacking: 'normal',
                                        borderRadius: 4,
                                        pointPadding: 0.08,
                                        groupPadding: 0.12,
                                        dataLabels: {
                                            enabled: true,
                                            style: {
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                textOutline: 'none',
                                                color: '#000'
                                            }
                                        }
                                    }
                                },
                                legend: {
                                    layout: 'horizontal',
                                    align: 'center',
                                    verticalAlign: 'bottom',
                                    y: 10,
                                    itemStyle: {
                                        fontSize: '13px',
                                        fontWeight: '500'
                                    }
                                },
                                series: [{
                                    name: 'Estimated Plates',
                                    data: popupData.map(item => parseFloat(item.estimated) || 0),
                                    color: '#FFA500',
                                    stack: 'estimated',  // Different stack to keep it separate
                                    dataLabels: {
                                        enabled: true,
                                        verticalAlign: 'top',
                                        y: -30,
                                        formatter: function() {
                                            return this.y > 0 ? this.y : '';
                                        }
                                    }
                                }, {
                                    name: 'Wastage Plates',
                                    data: popupData.map(item => parseFloat(item.wasted_plates) || 0),
                                    color: '#ef4444',
                                    stack: 'actual',
                                    dataLabels: {
                                        enabled: false  // Don't show label on red bar (middle)
                                    }
                                }, {
                                    name: 'Actual Plates Sold',
                                    data: popupData.map(item => parseFloat(item.actual_plates) || 0),
                                    color: '#22c55e',
                                    stack: 'actual',
                                    dataLabels: {
                                        enabled: false  // Disabled - using stackLabels instead
                                    }
                                }],
                                credits: {
                                    enabled: false
                                },
                                exporting: {
                                    enabled: false
                                }
                            }}
                        />
                        </div>
                    </div>
                )}
                {selectedGraphPopup === 'estimated-realtime' && (!popupData || !Array.isArray(popupData) || popupData.length === 0) && (
                    <NoDataPlaceholder
                        type="usage"
                        title="No usage data yet"
                        subtitle="Compare expected vs actual consumption once items start moving."
                        minHeight="320px"
                    />
                )}
            </GraphPopup>

            {/* Pickup Report Popup */}
            <GraphPopup
                isOpen={selectedGraphPopup === 'pickup-report'}
                onClose={closeGraphPopup}
                title={t('reports.stock.pickup.title') || 'Category Pickup Report'}
                extraClass="pickup-graph-popup"
            >
                {loadingPickup ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px',
                        padding: '40px 20px',
                        gap: '16px'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #3498db',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        <p style={{
                            color: '#666',
                            fontSize: '16px',
                            textAlign: 'center',
                            margin: 0
                        }}>Loading Pickup Data...</p>
                        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                    </div>
                ) : !pickupData || pickupData.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        No pickup data available for the selected date.
                    </div>
                ) : (
                    <div style={{ width: '100%', height: '500px', overflowX: 'auto', overflowY: 'hidden' }}>
                        <div style={{ minWidth: `${pickupData.length * 200}px`, height: '100%' }}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={{
                                chart: { 
                                    type: 'column', 
                                    height: 500, 
                                    spacing: [10, 20, 10, 20],
                                    width: pickupData.length * 200
                                },
                                title: { text: null },
                                xAxis: {
                                    categories: pickupData.map(i => i.item_name || 'Unknown'),
                                    labels: {
                                        rotation: -45,
                                        align: 'right',
                                        style: { fontSize: '12px', color: '#374151', fontWeight: '500' },
                                        step: 1,
                                        useHTML: false
                                    }
                                },
                                yAxis: {
                                    min: 0,
                                    title: { text: 'Plates', style: { fontSize: '12px', fontWeight: '600' } }
                                },
                                tooltip: {
                                    shared: true,
                                    useHTML: true,
                                    formatter: function() {
                                        let tt = `<b>${this.x}</b><br/>`;
                                        this.points.forEach(p => {
                                            tt += `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y}</b> plates<br/>`;
                                        });
                                        return tt;
                                    }
                                },
                                plotOptions: {
                                    column: {
                                        pointPadding: 0.1,
                                        borderWidth: 0,
                                        borderRadius: 4,
                                        groupPadding: 0.6,
                                        pointWidth: 30,
                                        dataLabels: {
                                            enabled: true,
                                            style: { fontSize: '10px', fontWeight: 'bold', textOutline: 'none', color: '#111827' },
                                            formatter: function() {
                                                return this.y > 0 ? this.y : '';
                                            }
                                        }
                                    }
                                },
                                legend: {
                                    align: 'center',
                                    verticalAlign: 'bottom',
                                    itemStyle: { fontSize: '12px', fontWeight: '500', color: '#374151' }
                                },
                                series: [
                                    {
                                        name: 'Estimated',
                                        data: pickupData.map(i => {
                                            const val = parseFloat(i.estimated_plates || 0);
                                            return isNaN(val) ? 0 : val;
                                        }),
                                        color: '#3b82f6'
                                    },
                                    {
                                        name: 'Actual',
                                        data: pickupData.map(i => {
                                            const val = parseFloat(i.actual_plates || 0);
                                            return isNaN(val) ? 0 : val;
                                        }),
                                        color: '#22c55e'
                                    }
                                ],
                                exporting: { enabled: false },
                                credits: { enabled: false }
                            }}
                        />
                        </div>
                    </div>
                )}
            </GraphPopup>
        </>
    );
};
